import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isFeatureEnabled } from '../config/featureFlags';
import TransactionDetailView from '../components/TransactionDetailView';
import { ProfessionalDirectoryManager, ProfessionalDirectoryPicker } from '../components/ProfessionalDirectory';
import {
  getPartyCorrectionTab,
  getProfessionalPartyInputPolicy,
  getSalesStatusEditPolicy,
  normalizeFinancingType,
  shouldValidatePartyForm,
  validatePartyForm,
  type MissingPartyField,
} from '../utils/transactionPartyCompliance';

const HOUR_MS = 60 * 60 * 1000;
const API_FETCH_TIMEOUT_MS = 15000;
const SUMMARY_FETCH_TIMEOUT_MS = 12000;
const SUMMARY_STALE_MS = 5 * 60 * 1000;

async function fetchWithTimeout(input: string, init: RequestInit, timeoutMs = API_FETCH_TIMEOUT_MS): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  if (init.signal) {
    if (init.signal.aborted) controller.abort();
    init.signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function msUntilNextHour(): number {
  const now = Date.now();
  const remainder = now % HOUR_MS;
  return remainder === 0 ? HOUR_MS : HOUR_MS - remainder;
}

type TransactionAgent = {
  associate_id: number | null;
  associate_name: string | null;
  image_url: string | null;
  source_associate_id: string | null;
  agent_role: string | null;
  split_percentage: number | null;
  outside_agency?: {
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    phone?: string | null;
    agency_name?: string | null;
  } | null;
  summary?: {
    office_name: string | null;
    transaction_type: string | null;
    split_percentage: string | null;
    variance_sale_list_pct: string | null;
    transaction_gci_before_fees: string | null;
    average_commission_pct: string | null;
    production_royalties: string | null;
    growth_share: string | null;
    total_pr_and_gs: string | null;
    gci_after_fees_excl_vat: string | null;
    associate_dollar: string | null;
    cap_amount: string | null;
    cap_contribution?: string | null;
    cap_remaining: string | null;
    team_dollar: string | null;
    market_center_dollar: string | null;
    is_outside_agent: boolean;
  };
};

type MonthEndParityRow = {
  market_center_name: string;
  contracts: number;
  total_gci: number;
};

type MonthEndParityResponse = {
  rows: MonthEndParityRow[];
  totals: {
    contracts: number;
    total_gci: number;
  };
};

type MarketCenterOption = {
  id: string;
  source_market_center_id: string;
  name: string;
  status_name: string | null;
};

type MarketCentersResponse = {
  total: number;
  limit: number;
  offset: number;
  items: MarketCenterOption[];
};

function formatIsoDate(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

type TransactionRow = {
  id: string;
  source_transaction_id: string;
  transaction_number: string | null;
  agents: TransactionAgent[];
  transaction_category?: string | null;
  source_type?: string | null;
  source_rental_id?: string | number | null;
  source_rental_payment_schedule_id?: string | number | null;
  counts_toward_cap?: boolean;
  source_market_center_id?: string | null;
  can_edit?: boolean;
  transaction_status: string | null;
  transaction_type: string | null;
  listing_number: string | null;
  source_listing_id: string | null;
  address: string | null;
  suburb: string | null;
  city: string | null;
  sales_price: string | null;
  list_price: string | null;
  gci_excl_vat: string | null;
  net_comm: string | null;
  total_gci: string | null;
  sale_type: string | null;
  buyer: string | null;
  seller: string | null;
  transfer_attorney?: string | null;
  ta_mobile_phone?: string | null;
  ta_email?: string | null;
  bond_attorney?: string | null;
  ba_mobile_phone?: string | null;
  ba_email?: string | null;
  bond_originator?: string | null;
  transaction_financing_type?: string | null;
  all_parties_invoiced?: string | null;
  party_contact_details?: Record<string, unknown> | null;
  list_date: string | null;
  transaction_date: string | null;
  status_change_date: string | null;
  expected_date: string | null;
  created_at?: string | null;
  market_center_name: string | null;
  updated_at: string;
};

type TransactionsResponse = {
  total: number;
  limit: number;
  offset: number;
  items: TransactionRow[];
};

type MonthEndPreviewItem = {
  transaction_agent_id: string;
  transaction_id: string;
  transaction_number: string | null;
  associate_name: string | null;
  market_center_name: string | null;
  listing_number: string | null;
  sales_price: number;
  gci_amount: number | null;
  effective_reporting_date: string;
  transaction_type: string | null;
  agent_role: string | null;
  frontdoor_org_id: string | null;
  kwuid: string | null;
  region_transaction_id: string;
  status: 'ready' | 'error';
  error: string | null;
  last_submission_status: string | null;
  last_submission_error: string | null;
  last_submitted_at: string | null;
  submitted_count: number;
};

type MonthEndPreviewResponse = {
  date_from: string;
  date_to: string;
  total_candidates: number;
  ready_to_submit: number;
  will_be_skipped: number;
  limit_applied: number;
  details_included: boolean;
  filters_applied?: {
    preview_status?: string;
    fed_status?: string;
    selected_transaction_agent_ids_count?: number;
  };
  items: MonthEndPreviewItem[];
};

type MonthEndSubmitResponse = {
  run_id: string;
  mode: 'monthly';
  date_from: string;
  date_to: string;
  limit: number;
  total: number;
  submitted: number;
  failed: number;
  skipped: number;
  selected_transaction_agent_ids_count?: number;
  preview_status?: string;
};

type TransactionsSummaryResponse = {
  totals: {
    total_transactions: number;
    total_sales_value: number;
    total_net_commission: number;
    average_split_percentage: number;
  };
  mtd_registered_active: {
    total_transactions: number;
    total_sales_value: number;
    total_net_commission: number;
    average_split_percentage: number;
  };
  by_status: Array<{ label: string; count: number }>;
  by_type: Array<{ label: string; count: number }>;
  market_center_performance: Array<{
    market_center: string;
    total_transactions: number;
    total_sales_value: number;
    total_gci: number;
  }>;
  associate_performance: Array<{
    associate_name: string;
    team_name: string;
    market_center: string;
    total_transactions: number;
    total_sales_value: number;
    total_gci: number;
  }>;
  team_performance: Array<{
    team_name: string;
    market_center: string;
    total_transactions: number;
    total_sales_value: number;
    total_gci: number;
  }>;
  expected_closings_90_days: Array<{ bucket: string; count: number; total_gci: number }>;
  reporting_window?: {
    start_date: string;
    end_date: string;
    basis: 'registered' | 'allStatuses';
  } | null;
  performance_basis?: 'registered' | 'allStatuses';
};

type CalculatedSummaryItem = {
  id: string;
  transaction_id: string;
  transaction_agent_id: string;
  associate_id: string | null;
  source_associate_id: string | null;
  is_outside_agent: boolean;
  agent_name: string | null;
  office_name: string | null;
  transaction_side: string | null;
  split_percentage: string | null;
  variance_sale_list_pct: string | null;
  transaction_gci_before_fees: string | null;
  average_commission_pct: string | null;
  production_royalties: string | null;
  growth_share: string | null;
  total_pr_and_gs: string | null;
  gci_after_fees_excl_vat: string | null;
  associate_dollar: string | null;
  cap_amount: string | null;
  cap_contribution?: string | null;
  cap_remaining: string | null;
  current_cap_remaining?: string | null;
  display_cap_remaining?: string | null;
  team_dollar: string | null;
  market_center_dollar: string | null;
};

type CalculatedSummaryResponse = {
  items: CalculatedSummaryItem[];
};

type TransactionDocumentRow = {
  id: string;
  transaction_id: string;
  source_document_id: string | null;
  source_transaction_document_type_id: string | null;
  transaction_document_type: string | null;
  file_name: string;
  document_url: string | null;
  preview_url: string | null;
  created_at: string | null;
  updated_at: string | null;
};

type OutsideAgencyContact = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  agency_name: string;
};

type PartyContactForm = {
  first_name: string;
  last_name: string;
  contact_number: string;
  email_address: string;
  company_name: string;
  directory_id?: string;
};

type ListingSearchResult = {
  id: string;
  source_listing_id: string;
  listing_number: string;
  address: string | null;
  suburb: string | null;
  city: string | null;
  list_price: string | null;
};

type ListingSearchResponse = { items: ListingSearchResult[] };

type TransactionFormAgent = {
  source_associate_id: string;
  associate_name: string;
  agent_role: string;
  split_percentage: string;
  outside_agency: OutsideAgencyContact;
};

type TransactionFormState = {
  source_transaction_id: string;
  transaction_number: string;
  transaction_status: string;
  status_change_date: string;
  transaction_type: string;
  source_listing_id: string;
  listing_number: string;
  address: string;
  suburb: string;
  city: string;
  sales_price: string;
  list_price: string;
  net_comm: string;
  total_gci: string;
  buyer: string;
  seller: string;
  transfer_attorney: string;
  transaction_financing_type: string;
  buyer_contacts: PartyContactForm[];
  seller_contacts: PartyContactForm[];
  transfer_attorney_contact: PartyContactForm;
  bond_attorney_contact: PartyContactForm;
  bond_originator_contact: PartyContactForm;
  bond_attorney: string;
  bond_originator: string;
  all_parties_invoiced: string;
  transaction_date: string;
  expected_date: string;
  agents: TransactionFormAgent[];
};

type TransactionEditTab = 'details' | 'parties' | 'documents' | 'notes' | 'emails' | 'summary';

type AgentOption = {
  id: string;
  source_associate_id: string;
  full_name: string | null;
  source_market_center_id: string | null;
  market_center_name: string | null;
};

type AgentOptionsResponse = {
  items: AgentOption[];
};

type CapRemainingPreviewItem = {
  associate_id: string;
  source_associate_id: string;
  team_id: string | null;
  associate_split_pct: string | null;
  team_commission_split_to_team: string | null;
  cap_amount: string;
  cap_remaining: string;
};

type CapRemainingPreviewResponse = {
  items: CapRemainingPreviewItem[];
};

type StagedTransactionDocument = {
  id: string;
  file: File;
  transaction_document_type: string;
  created_at: string;
};

const TRANSACTION_STATUSES = [
  'Start',
  'Working',
  'Submitted',
  'Registered',
  'Accepted',
  'Rejected',
  'Withdrawn',
  'Pending',
];

const TRANSACTION_TYPE_FILTER_OPTIONS = [
  'Buyer',
  'Seller',
  'Both',
  'Referral',
  'Other',
  'Outside Agent',
  'Outside Agency',
] as const;

const AGENT_ROLES = ['Seller', 'Buyer', 'Both', 'PC', 'Outside Agent', 'Other'] as const;

function getRoleOptions(currentRole: string): string[] {
  if (!currentRole) return [...AGENT_ROLES];
  return AGENT_ROLES.includes(currentRole as (typeof AGENT_ROLES)[number])
    ? [...AGENT_ROLES]
    : [currentRole, ...AGENT_ROLES];
}

const DEFAULT_PAGE_SIZE = 25;
type TransactionsView = 'summary' | 'register' | 'month_end';
type RegisterDateFilter = '' | 'transaction_date' | 'status_change_date';
type RegisterSortKey =
  | 'transaction'
  | 'associate'
  | 'market_center'
  | 'type'
  | 'status'
  | 'created_date'
  | 'status_change_date'
  | 'sales_price'
  | 'net_comm';
type MonthEndSortKey =
  | 'status'
  | 'transaction'
  | 'associate'
  | 'market_center'
  | 'listing'
  | 'gci'
  | 'sales_price'
  | 'reporting_date'
  | 'fed_status'
  | 'fed_at';
type SortDirection = 'asc' | 'desc';
type TransactionCategoryView = 'sales' | 'rentals';

function toMoney(val: string | null): string {
  if (!val) return '-';
  const n = Number(val);
  if (!Number.isFinite(n)) return '-';
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 }).format(n);
}

function toShortDate(val: string | null): string {
  if (!val) return '-';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
}

function toShortDateTime(val: string | null): string {
  if (!val) return '-';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString();
}

function getPreviousMonthWindow(): { from: string; to: string } {
  const now = new Date();
  const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
  return {
    from: prevMonthStart.toISOString().slice(0, 10),
    to: prevMonthEnd.toISOString().slice(0, 10),
  };
}

function toNumberOrZero(value: string | null | undefined): number {
  if (!value) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function normalizeRole(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function isContactPlaceholder(value: string | null | undefined): boolean {
  return /^CONTACT:/i.test((value ?? '').trim());
}

function isOutsideRole(value: string): boolean {
  return value.includes('outside');
}

function isOutsideAgentRole(value: string | null | undefined): boolean {
  const normalized = normalizeRole(value);
  return normalized === 'outside agency referral' || normalized === 'outside agent' || normalized === 'outside agency';
}

function normalizeAgentRoleForForm(value: string | null | undefined): string {
  const normalized = normalizeRole(value);
  if (normalized === 'outside agent' || normalized === 'outside agency referral' || normalized === 'outside agency') return 'Outside Agent';
  return value?.trim() ?? '';
}

function getOutsideAgencyDisplayName(outsideAgency: TransactionAgent['outside_agency'] | TransactionFormAgent['outside_agency'] | null | undefined): string {
  const fullName = joinContactName(outsideAgency?.first_name ?? '', outsideAgency?.last_name ?? '');
  if (fullName.length > 0) return fullName;
  const agencyName = (outsideAgency?.agency_name ?? '').trim();
  if (agencyName.length > 0) return agencyName;
  return '';
}

function getTransactionAgentDisplayName(agent: TransactionAgent): string {
  const outsideDisplayName = getOutsideAgencyDisplayName(agent.outside_agency);
  const normalizedRole = normalizeRole(agent.agent_role ?? agent.summary?.transaction_type ?? null);
  const associateName = (agent.associate_name ?? '').trim();
  const sourceAssociateId = (agent.source_associate_id ?? '').trim();

  if (isOutsideRole(normalizedRole) && outsideDisplayName.length > 0) return outsideDisplayName;
  if (associateName.length > 0 && !isContactPlaceholder(associateName)) return associateName;
  if (outsideDisplayName.length > 0) return outsideDisplayName;
  if (sourceAssociateId.length > 0 && !isContactPlaceholder(sourceAssociateId)) return sourceAssociateId;
  if (associateName.length > 0) return associateName;
  if (sourceAssociateId.length > 0) return sourceAssociateId;
  return '-';
}

function getOutsideAgentDisplayName(agent: TransactionFormAgent): string {
  if (!isOutsideAgentRole(agent.agent_role)) return agent.associate_name;
  const outsideDisplayName = getOutsideAgencyDisplayName(agent.outside_agency);
  if (outsideDisplayName.length > 0) return outsideDisplayName;
  if (!isContactPlaceholder(agent.associate_name)) return agent.associate_name;
  return '';
}

function splitNameParts(value: string | null | undefined): { first: string; last: string } {
  const clean = (value ?? '').trim();
  if (clean.length === 0) return { first: '', last: '' };
  const parts = clean.split(/\s+/);
  return {
    first: parts[0] ?? '',
    last: parts.slice(1).join(' '),
  };
}

function toRoleLabel(value: string): string {
  if (value === 'both') return 'Both';
  if (value === 'seller') return 'Seller';
  if (value === 'buyer') return 'Buyer';
  if (value.length === 0) return '-';
  return value
    .split(' ')
    .map((part) => (part.length > 0 ? part[0].toUpperCase() + part.slice(1) : part))
    .join(' ');
}

function getDerivedTransactionType(formType: string, agents: TransactionFormAgent[]): string {
  const explicit = formType.trim();
  if (explicit.length > 0) {
    return explicit;
  }

  const roles = new Set<string>();
  let hasOutsideRole = false;

  for (const agent of agents) {
    const role = normalizeRole(agent.agent_role);
    if (role.length === 0) {
      continue;
    }
    if (isOutsideAgentRole(role)) {
      hasOutsideRole = true;
      continue;
    }
    roles.add(role);
  }

  if (roles.has('both') || (roles.has('buyer') && roles.has('seller'))) return 'Both';
  if (roles.has('buyer')) return 'Buyer';
  if (roles.has('seller')) return 'Seller';
  if (roles.has('referral')) return 'Referral';
  if (roles.has('other')) return 'Other';
  if (hasOutsideRole) return 'Outside Agency';
  return '';
}

function getDisplayTransactionType(item: TransactionRow): string {
  const explicit = (item.transaction_type ?? '').trim();
  const explicitNormalized = normalizeRole(explicit);

  const internalRoles = new Set<string>();
  let hasOutsideRole = false;

  for (const agent of item.agents ?? []) {
    const role = normalizeRole(agent.agent_role ?? agent.summary?.transaction_type ?? null);
    if (role.length === 0) {
      continue;
    }
    if (isOutsideRole(role)) {
      hasOutsideRole = true;
      continue;
    }
    internalRoles.add(role);
  }

  let inferredInternalType = '';
  if (internalRoles.has('both') || (internalRoles.has('seller') && internalRoles.has('buyer'))) {
    inferredInternalType = 'Both';
  } else if (internalRoles.has('seller')) {
    inferredInternalType = 'Seller';
  } else if (internalRoles.has('buyer')) {
    inferredInternalType = 'Buyer';
  } else if (internalRoles.size === 1) {
    inferredInternalType = toRoleLabel(Array.from(internalRoles)[0]);
  }

  if (explicit.length > 0) {
    if (isOutsideRole(explicitNormalized) && inferredInternalType.length > 0) {
      return inferredInternalType;
    }
    return explicit;
  }

  if (inferredInternalType.length > 0) {
    return inferredInternalType;
  }

  if (hasOutsideRole) {
    return 'Outside Agency';
  }

  return '-';
}

function isRentalTransactionItem(item: TransactionRow | null | undefined): boolean {
  if (!item) return false;
  const category = (item.transaction_category ?? '').trim().toLowerCase();
  const sourceType = (item.source_type ?? '').trim().toLowerCase();
  return category === 'rentals'
    || sourceType === 'rental_payment'
    || item.source_rental_id != null
    || item.source_rental_payment_schedule_id != null;
}

function toPercent(value: number): string {
  return `${value.toFixed(2)}%`;
}

function roundMoney(value: number): number {
  return Math.round((Number.isFinite(value) ? value : 0) * 100) / 100;
}

function toDecimalText(value: number): string {
  return roundMoney(value).toFixed(2);
}

function normalizeAssociateSplitPct(rawSplit: number): number {
  if (rawSplit <= 0) return 70;
  if (rawSplit <= 1) return rawSplit * 100;
  return Math.max(Math.min(rawSplit, 100), 0);
}

function normalizeTeamSplitPct(rawSplit: number): number {
  if (rawSplit <= 0) return 0;
  if (rawSplit <= 1) return Math.max(Math.min(rawSplit * 100, 100), 0);
  return Math.max(Math.min(rawSplit, 100), 0);
}

function normalizeSummarySide(value: string | null | undefined, fallback: string): string {
  const clean = (value ?? '').trim();
  if (clean.length > 0) return clean;
  return fallback;
}

function buildPreviewCalculatedRows(
  form: TransactionFormState,
  transactionId: string,
  officeFallback: string | null,
  derivedTransactionType: string,
  capRemainingBySourceAssociateId: Map<string, {
    cap_amount: number;
    cap_remaining: number;
    team_id: string | null;
    associate_split_pct: number;
    team_commission_split_to_team: number;
  }>,
  persistedSplitOverrideBySourceAssociateId: Map<string, {
    non_mc_split_pct: number;
  }> = new Map()
): CalculatedSummaryItem[] {
  const totalGci = Math.max(toNumberOrZero(form.total_gci), 0);
  const salePrice = Math.max(toNumberOrZero(form.sales_price), 0);
  const listPrice = Math.max(toNumberOrZero(form.list_price), 0);
  const varianceSaleListPct = listPrice > 0 ? ((salePrice - listPrice) / listPrice) * 100 : 0;
  const baseAverageCommissionPct = salePrice > 0 ? (totalGci / salePrice) * 100 : 0;
  const projectedCapRemainingByKey = new Map<string, number>();

  return form.agents
    .map((agent, index) => {
      const splitPercentage = Math.max(toNumberOrZero(agent.split_percentage), 0);
      const splitRatio = splitPercentage / 100;
      const transactionGciBeforeFees = roundMoney(totalGci * splitRatio);
      const productionRoyalties = roundMoney(transactionGciBeforeFees * 0.06);
      const growthShare = roundMoney(transactionGciBeforeFees * 0.02);
      const totalPrAndGs = roundMoney(productionRoyalties + growthShare);
      const gciAfterFeesExclVat = roundMoney(transactionGciBeforeFees * 0.92);
      const displayAgentName = getOutsideAgentDisplayName(agent).trim() || agent.associate_name.trim() || '-';
      const roleLabel = normalizeSummarySide(agent.agent_role, derivedTransactionType || '-');
      const isOutsideAgent = isOutsideAgentRole(agent.agent_role);
      const capLookup = capRemainingBySourceAssociateId.get(agent.source_associate_id);
      const isTeamTransaction = Boolean(capLookup?.team_id);
      const capProjectionKey = isTeamTransaction
        ? `team:${capLookup?.team_id ?? ''}`
        : `associate:${agent.source_associate_id}`;
      const currentProjectedCapRemaining = projectedCapRemainingByKey.has(capProjectionKey)
        ? projectedCapRemainingByKey.get(capProjectionKey) ?? 0
        : Math.max(capLookup?.cap_remaining ?? 0, 0);
      const effectiveCapRemaining = Math.max(currentProjectedCapRemaining, 0);
      const persistedSplitOverride = persistedSplitOverrideBySourceAssociateId.get(agent.source_associate_id);
      const configuredTeamSplit = normalizeTeamSplitPct(capLookup?.team_commission_split_to_team ?? 0);
      const associateSplitPct = isOutsideAgent
        ? 100
        : persistedSplitOverride != null
          ? Math.max(Math.min(persistedSplitOverride.non_mc_split_pct, 100), 0)
        : isTeamTransaction && configuredTeamSplit > 0
          ? configuredTeamSplit
          : normalizeAssociateSplitPct(capLookup?.associate_split_pct ?? 0);
      const marketCenterSplitPct = Math.max(100 - associateSplitPct, 0);
      const associateDollarPreCap = roundMoney(gciAfterFeesExclVat * (associateSplitPct / 100));
      const marketCenterDollar = roundMoney(gciAfterFeesExclVat * (marketCenterSplitPct / 100));

      let adjustedAssociateDollar = isTeamTransaction ? 0 : associateDollarPreCap;
      let adjustedTeamDollar = isTeamTransaction ? associateDollarPreCap : 0;
      let adjustedMarketCenterDollar = marketCenterDollar;
      let projectedCapRemaining = effectiveCapRemaining;

      if (marketCenterDollar > 0) {
        if (effectiveCapRemaining <= 0) {
          const overflow = marketCenterDollar;
          adjustedMarketCenterDollar = 0;
          projectedCapRemaining = 0;
          if (isTeamTransaction) {
            adjustedTeamDollar = roundMoney(adjustedTeamDollar + overflow);
          } else {
            adjustedAssociateDollar = roundMoney(adjustedAssociateDollar + overflow);
          }
        } else if (marketCenterDollar > effectiveCapRemaining) {
          const overflow = roundMoney(marketCenterDollar - effectiveCapRemaining);
          adjustedMarketCenterDollar = roundMoney(effectiveCapRemaining);
          projectedCapRemaining = 0;
          if (isTeamTransaction) {
            adjustedTeamDollar = roundMoney(adjustedTeamDollar + overflow);
          } else {
            adjustedAssociateDollar = roundMoney(adjustedAssociateDollar + overflow);
          }
        } else {
          projectedCapRemaining = roundMoney(effectiveCapRemaining - adjustedMarketCenterDollar);
        }
      }

      projectedCapRemainingByKey.set(capProjectionKey, Math.max(projectedCapRemaining, 0));

      return {
        id: `preview-${transactionId}-${index}`,
        transaction_id: transactionId,
        transaction_agent_id: `preview-agent-${index}`,
        associate_id: null,
        source_associate_id: agent.source_associate_id || null,
        is_outside_agent: isOutsideAgent,
        agent_name: displayAgentName,
        office_name: officeFallback,
        transaction_side: roleLabel,
        split_percentage: toDecimalText(splitPercentage),
        variance_sale_list_pct: toDecimalText(varianceSaleListPct),
        transaction_gci_before_fees: toDecimalText(transactionGciBeforeFees),
        average_commission_pct: toDecimalText(baseAverageCommissionPct),
        production_royalties: toDecimalText(productionRoyalties),
        growth_share: toDecimalText(growthShare),
        total_pr_and_gs: toDecimalText(totalPrAndGs),
        gci_after_fees_excl_vat: toDecimalText(gciAfterFeesExclVat),
        associate_dollar: toDecimalText(adjustedAssociateDollar),
        cap_amount: toDecimalText(capLookup?.cap_amount ?? 0),
        cap_contribution: toDecimalText(adjustedMarketCenterDollar),
        cap_remaining: toDecimalText(projectedCapRemaining),
        current_cap_remaining: toDecimalText(projectedCapRemaining),
        display_cap_remaining: toDecimalText(projectedCapRemaining),
        team_dollar: toDecimalText(adjustedTeamDollar),
        market_center_dollar: toDecimalText(adjustedMarketCenterDollar),
      };
    })
    .filter((row) => row.agent_name !== '-' && toNumberOrZero(row.split_percentage) > 0);
}

function buildSnapshotCalculatedRows(transaction: TransactionRow | null | undefined): CalculatedSummaryItem[] {
  if (!transaction) return [];

  return (transaction.agents ?? [])
    .filter((agent) => Boolean(agent.summary))
    .map((agent, index) => ({
      id: `snapshot-${transaction.id}-${index}`,
      transaction_id: String(transaction.id),
      transaction_agent_id: `snapshot-agent-${index}`,
      associate_id: agent.associate_id != null ? String(agent.associate_id) : null,
      source_associate_id: agent.source_associate_id,
      is_outside_agent: Boolean(agent.summary?.is_outside_agent),
      agent_name: getTransactionAgentDisplayName(agent),
      office_name: agent.summary?.office_name ?? transaction.market_center_name,
      transaction_side: agent.agent_role ?? agent.summary?.transaction_type ?? null,
      split_percentage: agent.summary?.split_percentage ?? '0',
      variance_sale_list_pct: agent.summary?.variance_sale_list_pct ?? '0',
      transaction_gci_before_fees: agent.summary?.transaction_gci_before_fees ?? '0',
      average_commission_pct: agent.summary?.average_commission_pct ?? '0',
      production_royalties: agent.summary?.production_royalties ?? '0',
      growth_share: agent.summary?.growth_share ?? '0',
      total_pr_and_gs: agent.summary?.total_pr_and_gs ?? '0',
      gci_after_fees_excl_vat: agent.summary?.gci_after_fees_excl_vat ?? '0',
      associate_dollar: agent.summary?.associate_dollar ?? '0',
      cap_amount: agent.summary?.cap_amount ?? '0',
      cap_contribution: agent.summary?.cap_contribution ?? agent.summary?.market_center_dollar ?? '0',
      cap_remaining: agent.summary?.cap_remaining ?? '0',
      team_dollar: agent.summary?.team_dollar ?? '0',
      market_center_dollar: agent.summary?.market_center_dollar ?? '0',
    }));
}

function buildFinancialSignature(rows: CalculatedSummaryItem[]): string {
  const totals = rows.reduce((acc, row) => {
    acc.gciAfterFees += toNumberOrZero(row.gci_after_fees_excl_vat);
    acc.associate += toNumberOrZero(row.associate_dollar);
    acc.mc += toNumberOrZero(row.market_center_dollar);
    acc.team += toNumberOrZero(row.team_dollar);
    acc.capRemaining += toNumberOrZero(row.display_cap_remaining ?? row.current_cap_remaining ?? row.cap_remaining);
    return acc;
  }, { gciAfterFees: 0, associate: 0, mc: 0, team: 0, capRemaining: 0 });

  return [
    rows.length,
    roundMoney(totals.gciAfterFees).toFixed(2),
    roundMoney(totals.associate).toFixed(2),
    roundMoney(totals.mc).toFixed(2),
    roundMoney(totals.team).toFixed(2),
    roundMoney(totals.capRemaining).toFixed(2),
  ].join('|');
}

function UiIcon({
  kind,
  className = 'h-4 w-4',
}: {
  kind:
    | 'sales'
    | 'rentals'
    | 'summary'
    | 'register'
    | 'filters'
    | 'window'
    | 'tx'
    | 'value'
    | 'gci'
    | 'split'
    | 'expected'
    | 'net'
    | 'calendar'
    | 'calendar-clock'
    | 'hourglass'
    | 'timeline'
    | 'eye'
    | 'edit'
    | 'file';
  className?: string;
}) {
  if (kind === 'sales') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <path d="M4 12h16M8 8l-4 4 4 4M16 8l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (kind === 'rentals') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <path d="M3 10.5 12 4l9 6.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5.5 9.5V21h13V9.5" stroke="currentColor" strokeWidth="1.7" />
      </svg>
    );
  }
  if (kind === 'summary') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <path d="M5 19V9M12 19V5M19 19v-7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === 'register') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <rect x="5" y="4" width="14" height="16" rx="2" stroke="currentColor" strokeWidth="1.7" />
        <path d="M8 9h8M8 13h8M8 17h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === 'filters') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <path d="M4 7h16M7 12h10M10 17h4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === 'window') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="1.7" />
        <path d="M8 3v4M16 3v4M4 10h16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === 'tx') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="1.7" />
        <path d="m8.5 12 2 2 5-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (kind === 'value') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <path d="M4 16.5 10 10.5l4 4L20 8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16 8h4v4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (kind === 'gci') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
        <path d="M12 7v10M9.5 9.5c0-1.1 1-2 2.5-2s2.5.9 2.5 2-1 2-2.5 2-2.5.9-2.5 2 1 2 2.5 2 2.5-.9 2.5-2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === 'split') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <path d="M19 5 5 19M8 7h.01M16 17h.01" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === 'expected') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="1.7" />
        <path d="M8 3v4M16 3v4M4 10h16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        <path d="m9.5 14 1.7 1.7 3.3-3.3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (kind === 'net') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <path d="M3 7.5A2.5 2.5 0 0 1 5.5 5h13A2.5 2.5 0 0 1 21 7.5v9a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 16.5Z" stroke="currentColor" strokeWidth="1.7" />
        <path d="M21 10h-6a2 2 0 0 0 0 4h6" stroke="currentColor" strokeWidth="1.7" />
      </svg>
    );
  }
  if (kind === 'calendar') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="1.7" />
        <path d="M8 3v4M16 3v4M4 10h16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === 'calendar-clock') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="1.7" />
        <path d="M8 3v4M16 3v4M4 10h16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        <path d="M12 12v3l2 1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === 'hourglass') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <path d="M7 4h10M7 20h10M8 4c0 4 2 4 4 6-2 2-4 2-4 6M16 4c0 4-2 4-4 6 2 2 4 2 4 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === 'timeline') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <path d="M4 12h16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        <circle cx="7" cy="12" r="1.5" fill="currentColor" />
        <circle cx="12" cy="12" r="1.5" fill="currentColor" />
        <circle cx="17" cy="12" r="1.5" fill="currentColor" />
      </svg>
    );
  }
  if (kind === 'eye') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" stroke="currentColor" strokeWidth="1.7" />
        <circle cx="12" cy="12" r="2.8" stroke="currentColor" strokeWidth="1.7" />
      </svg>
    );
  }
  if (kind === 'edit') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
        <path d="m4 20 4.5-1 9-9-3.5-3.5-9 9L4 20Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
        <path d="m12.5 6.5 3.5 3.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <rect x="5" y="4" width="14" height="16" rx="2" stroke="currentColor" strokeWidth="1.7" />
      <path d="M8 9h8M8 13h8M8 17h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function transactionStatusClass(status: string | null | undefined): string {
  const normalized = (status ?? '').trim().toLowerCase();
  if (normalized === 'registered') return 'bg-emerald-100 text-emerald-700';
  if (normalized === 'submitted') return 'bg-blue-100 text-blue-700';
  if (normalized === 'start') return 'bg-slate-100 text-slate-700';
  if (normalized === 'working') return 'bg-amber-100 text-amber-700';
  if (normalized === 'pending') return 'bg-violet-100 text-violet-700';
  if (normalized === 'withdrawn') return 'bg-rose-100 text-rose-700';
  if (normalized === 'rejected') return 'bg-red-100 text-red-700';
  if (normalized === 'accepted') return 'bg-cyan-100 text-cyan-700';
  return 'bg-slate-100 text-slate-700';
}

function buildEmptyOutsideAgency(): OutsideAgencyContact {
  return { first_name: '', last_name: '', email: '', phone: '', agency_name: '' };
}

function buildEmptyPartyContact(): PartyContactForm {
  return { first_name: '', last_name: '', contact_number: '', email_address: '', company_name: '' };
}

function normalizePartyContact(input: unknown): PartyContactForm {
  if (!input || typeof input !== 'object') return buildEmptyPartyContact();
  const value = input as Partial<PartyContactForm> & Record<string, unknown>;
  return {
    first_name: typeof value.first_name === 'string' ? value.first_name : '',
    last_name: typeof value.last_name === 'string' ? value.last_name : '',
    contact_number: typeof value.contact_number === 'string'
      ? value.contact_number
      : typeof value.phone === 'string'
        ? value.phone
        : '',
    email_address: typeof value.email_address === 'string'
      ? value.email_address
      : typeof value.email === 'string'
        ? value.email
        : '',
    company_name: typeof value.company_name === 'string' ? value.company_name : '',
    directory_id: typeof value.directory_id === 'string' ? value.directory_id : undefined,
  };
}

function normalizePartyContactList(input: unknown, fallbackName?: string | null): PartyContactForm[] {
  if (Array.isArray(input)) {
    const normalized = input.map((entry) => normalizePartyContact(entry));
    if (normalized.length > 0) return normalized;
  }

  if (input && typeof input === 'object') {
    return [normalizePartyContact(input)];
  }

  const fallback = (fallbackName ?? '').trim();
  if (fallback.length > 0) {
    const parts = fallback.split(/\s+/);
    return [{
      ...buildEmptyPartyContact(),
      first_name: parts[0] ?? '',
      last_name: parts.slice(1).join(' '),
    }];
  }

  return [buildEmptyPartyContact()];
}

function isPartyContactFilled(contact: PartyContactForm): boolean {
  return [contact.first_name, contact.last_name, contact.contact_number, contact.email_address, contact.company_name]
    .some((value) => value.trim().length > 0);
}

function compactPartyContactName(contact: PartyContactForm, fallbackLabel: string): string {
  const fullName = joinContactName(contact.first_name, contact.last_name);
  if (fullName.length > 0) return fullName;
  if (contact.company_name.trim().length > 0) return contact.company_name.trim();
  return fallbackLabel;
}

function sanitizePartyContacts(contacts: PartyContactForm[]): PartyContactForm[] {
  const filtered = contacts.filter((contact) => isPartyContactFilled(contact));
  return filtered.length > 0 ? filtered : [buildEmptyPartyContact()];
}

function joinContactName(firstName: string, lastName: string): string {
  return [firstName, lastName].map((part) => part.trim()).filter(Boolean).join(' ');
}

export default function TransactionsPage() {
  const { token, activeContext, isRegionalAdmin, isOfficeAdmin } = useAuth();
  const isTransactionsUxEnhanced = isFeatureEnabled('TRANSACTIONS_UX_ENHANCED');
  const normalizedSummaryRole = (activeContext?.role ?? '').trim().toLowerCase().replace(/[_\s]+/g, ' ');
  const isTeamSummaryRole = ['lead agent', 'team admin', 'team agent'].includes(normalizedSummaryRole);
  const isIndividualAgentSummaryRole = normalizedSummaryRole === 'agent';
  const isAdminSummaryRole = isRegionalAdmin || isOfficeAdmin;
  const location = useLocation();
  const navigate = useNavigate();
  const previousMonthWindow = useMemo(() => getPreviousMonthWindow(), []);
  const [categoryView, setCategoryView] = useState<TransactionCategoryView>('sales');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [agentQuickFilter, setAgentQuickFilter] = useState('');
  const [marketCenterQuickFilter, setMarketCenterQuickFilter] = useState('');
  const [copiedTransactionId, setCopiedTransactionId] = useState<string | null>(null);
  const [registerDateFilter, setRegisterDateFilter] = useState<RegisterDateFilter>('');
  const [registerDateFrom, setRegisterDateFrom] = useState('');
  const [registerDateTo, setRegisterDateTo] = useState('');
  const [view, setView] = useState<TransactionsView>('summary');
  const [showSummaryFilters, setShowSummaryFilters] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [detailViewId, setDetailViewId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTab, setEditTab] = useState<TransactionEditTab>('details');
  const [quickSummaryRow, setQuickSummaryRow] = useState<TransactionRow | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isRecalculatingSummary, setIsRecalculatingSummary] = useState(false);
  const [forceSummaryPreview, setForceSummaryPreview] = useState(false);
  const [summaryPreviewNotice, setSummaryPreviewNotice] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [partyValidationFields, setPartyValidationFields] = useState<MissingPartyField[]>([]);
  const [directoryDecisions, setDirectoryDecisions] = useState<Record<string, boolean>>({});
  const [directoryManagerOpen, setDirectoryManagerOpen] = useState(false);
  const [registerSortKey, setRegisterSortKey] = useState<RegisterSortKey>('status_change_date');
  const [registerSortDirection, setRegisterSortDirection] = useState<SortDirection>('desc');
  const [listingSearchQuery, setListingSearchQuery] = useState('');
  const [listingSearchOpen, setListingSearchOpen] = useState(false);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [documentTypeInput, setDocumentTypeInput] = useState('');
  const [documentActionError, setDocumentActionError] = useState<string | null>(null);
  const [stagedDocuments, setStagedDocuments] = useState<StagedTransactionDocument[]>([]);
  const [monthEndDateFrom, setMonthEndDateFrom] = useState(previousMonthWindow.from);
  const [monthEndDateTo, setMonthEndDateTo] = useState(previousMonthWindow.to);
  const [monthEndSearch, setMonthEndSearch] = useState('');
  const [monthEndMarketCenterFilter, setMonthEndMarketCenterFilter] = useState('');
  const [monthEndAssociateFilter, setMonthEndAssociateFilter] = useState('');
  const [monthEndListingFilter, setMonthEndListingFilter] = useState('');
  const [monthEndTransactionFilter, setMonthEndTransactionFilter] = useState('');
  const [monthEndPreviewStatusFilter, setMonthEndPreviewStatusFilter] = useState<'all' | 'ready' | 'error'>('all');
  const [monthEndFedStatusFilter, setMonthEndFedStatusFilter] = useState<'all' | 'never' | 'fed' | 'submitted' | 'failed' | 'skipped'>('all');
  const [isPreviewingMonthEnd, setIsPreviewingMonthEnd] = useState(false);
  const [isSubmittingMonthEnd, setIsSubmittingMonthEnd] = useState(false);
  const [monthEndError, setMonthEndError] = useState<string | null>(null);
  const [monthEndPreview, setMonthEndPreview] = useState<MonthEndPreviewResponse | null>(null);
  const [monthEndSubmission, setMonthEndSubmission] = useState<MonthEndSubmitResponse | null>(null);
  const [allowSubmitWithErrors, setAllowSubmitWithErrors] = useState(false);
  const [selectedMonthEndItemIds, setSelectedMonthEndItemIds] = useState<string[]>([]);
  const [monthEndSortKey, setMonthEndSortKey] = useState<MonthEndSortKey>('status');
  const [monthEndSortDirection, setMonthEndSortDirection] = useState<SortDirection>('asc');
  const [lastGoodTransactionsData, setLastGoodTransactionsData] = useState<TransactionsResponse | null>(null);
  const [lastGoodSummaryData, setLastGoodSummaryData] = useState<TransactionsSummaryResponse | null>(null);
  const [form, setForm] = useState<TransactionFormState>({
    source_transaction_id: '',
    transaction_number: '',
    transaction_status: 'Start',
    status_change_date: '',
    transaction_type: '',
    source_listing_id: '',
    listing_number: '',
    address: '',
    suburb: '',
    city: '',
    sales_price: '',
    list_price: '',
    net_comm: '',
    total_gci: '',
    buyer: '',
    seller: '',
    transfer_attorney: '',
    transaction_financing_type: '',
    buyer_contacts: [buildEmptyPartyContact()],
    seller_contacts: [buildEmptyPartyContact()],
    transfer_attorney_contact: buildEmptyPartyContact(),
    bond_attorney_contact: buildEmptyPartyContact(),
    bond_originator_contact: buildEmptyPartyContact(),
    bond_attorney: '',
    bond_originator: '',
    all_parties_invoiced: '',
    transaction_date: '',
    expected_date: '',
    agents: [],
  });

  const activeContextId = activeContext?.id ?? 'no-context';
  const authHeaders: Record<string, string> = {};
  if (token) authHeaders.Authorization = `Bearer ${token}`;
  if (activeContext?.id) authHeaders['X-Active-Context'] = activeContext.id;
  const normalizeForMatch = (value: string | null | undefined): string =>
    String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
  const activeOfficeNorm = normalizeForMatch(activeContext?.marketCenter ?? null);
  const queryClient = useQueryClient();

  const effectiveRegisterSearch = useMemo(() => {
    const filters = [search.trim()];
    if (isTransactionsUxEnhanced && agentQuickFilter.trim()) filters.push(agentQuickFilter.trim());
    if (isTransactionsUxEnhanced && marketCenterQuickFilter.trim()) filters.push(marketCenterQuickFilter.trim());
    return filters.filter(Boolean).join(' ').trim();
  }, [agentQuickFilter, isTransactionsUxEnhanced, marketCenterQuickFilter, search]);

  const { data: queryTransactionsData, isLoading, isError, refetch } = useQuery({
    queryKey: [
      'transactions',
      activeContextId,
      categoryView,
      page,
      pageSize,
      search,
      effectiveRegisterSearch,
      status,
      typeFilter,
      agentQuickFilter,
      marketCenterQuickFilter,
      registerDateFilter,
      registerDateFrom,
      registerDateTo,
    ],
    queryFn: async ({ signal }) => {
      const offset = (page - 1) * pageSize;
      const params = new URLSearchParams({ limit: String(pageSize), offset: String(offset) });
      if (effectiveRegisterSearch.trim()) params.set('search', effectiveRegisterSearch.trim());
      if (status.trim()) params.set('status', status.trim());
      if (typeFilter.trim()) params.set('type', typeFilter.trim());
      if (registerDateFilter) {
        params.set('date_filter', registerDateFilter);
        if (registerDateFrom) params.set('date_from', registerDateFrom);
        if (registerDateTo) params.set('date_to', registerDateTo);
      }
      params.set('category', categoryView);

      const response = await fetchWithTimeout(`/api/transactions?${params.toString()}`, {
        headers: authHeaders,
        signal,
        cache: 'no-store',
      });
      if (!response.ok) {
        throw new Error('Unable to load transactions');
      }
      return response.json() as Promise<TransactionsResponse>;
    },
    placeholderData: (prev) => prev,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: () => msUntilNextHour(),
    refetchOnWindowFocus: false,
    retry: 2,
  });

  useEffect(() => {
    if (queryTransactionsData) {
      setLastGoodTransactionsData(queryTransactionsData);
    }
  }, [queryTransactionsData]);

  const data = queryTransactionsData ?? lastGoodTransactionsData;

  function updateVisibleTransactionCache(transactionId: string, updates: Partial<TransactionRow>): void {
    const queryKey = [
      'transactions',
      activeContextId,
      categoryView,
      page,
      pageSize,
      search,
      effectiveRegisterSearch,
      status,
      typeFilter,
      agentQuickFilter,
      marketCenterQuickFilter,
      registerDateFilter,
      registerDateFrom,
      registerDateTo,
    ] as const;

    queryClient.setQueryData<TransactionsResponse | undefined>(queryKey, (current) => {
      if (!current) return current;
      return {
        ...current,
        items: current.items.map((item) => (item.id === transactionId ? { ...item, ...updates } : item)),
      };
    });

    setLastGoodTransactionsData((previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        items: previous.items.map((item) => (item.id === transactionId ? { ...item, ...updates } : item)),
      };
    });
  }

  async function refreshTransactionViews(): Promise<void> {
    if (view === 'summary' && !isRentalsCategory) {
      await Promise.allSettled([refetch(), refetchSummary()]);
      return;
    }
    await refetch();
  }

  useEffect(() => {
    setPage(1);
    void refetch();
  }, [activeContextId, refetch]);

  const { data: querySummaryData, isLoading: isSummaryLoading, isError: isSummaryError, refetch: refetchSummary } = useQuery({
    queryKey: ['transactions-summary', activeContextId, categoryView],
    queryFn: async ({ signal }) => {
      const response = await fetchWithTimeout(`/api/transactions/summary?category=${encodeURIComponent(categoryView)}`, {
        headers: authHeaders,
        signal,
        cache: 'no-store',
      }, SUMMARY_FETCH_TIMEOUT_MS);
      if (!response.ok) throw new Error('Unable to load transactions summary');
      return response.json() as Promise<TransactionsSummaryResponse>;
    },
    placeholderData: (prev) => prev,
    enabled: view === 'summary' && categoryView !== 'rentals',
    staleTime: SUMMARY_STALE_MS,
    refetchOnMount: false,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: (attempt) => Math.min(1500 * attempt, 5000),
  });

  useEffect(() => {
    if (querySummaryData) {
      setLastGoodSummaryData(querySummaryData);
    }
  }, [querySummaryData]);

  const summaryData = querySummaryData ?? lastGoodSummaryData;

  const monthEndParityWindow = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return {
      dateFrom: formatIsoDate(start),
      dateTo: formatIsoDate(now),
    };
  }, []);

  const { data: monthEndParityData } = useQuery({
    queryKey: ['transactions-month-end-parity', activeContextId, monthEndParityWindow.dateFrom, monthEndParityWindow.dateTo],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({
        date_from: monthEndParityWindow.dateFrom,
        date_to: monthEndParityWindow.dateTo,
        transaction_status: 'Registered',
        sale_type: 'For Sale',
      });
      const response = await fetchWithTimeout(`/api/reports/month-end?${params.toString()}`, {
        headers: authHeaders,
        signal,
        cache: 'no-store',
      });
      if (!response.ok) throw new Error('Unable to load month-end parity summary');
      return response.json() as Promise<MonthEndParityResponse>;
    },
    enabled: view === 'summary' && categoryView !== 'rentals',
    staleTime: SUMMARY_STALE_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: (attempt) => Math.min(1500 * attempt, 5000),
  });

  const { data: monthEndMarketCentersData, isFetching: isMonthEndMarketCentersLoading } = useQuery({
    queryKey: ['month-end-active-market-centers', activeContextId, monthEndMarketCenterFilter],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({
        status: 'active',
        limit: '50',
        offset: '0',
      });
      if (monthEndMarketCenterFilter.trim()) {
        params.set('search', monthEndMarketCenterFilter.trim());
      }

      const response = await fetchWithTimeout(`/api/market-centers?${params.toString()}`, {
        headers: authHeaders,
        signal,
        cache: 'no-store',
      });
      if (!response.ok) {
        throw new Error('Unable to load active market centres');
      }
      return response.json() as Promise<MarketCentersResponse>;
    },
    enabled: view === 'month_end' && categoryView !== 'rentals' && isRegionalAdmin,
    staleTime: SUMMARY_STALE_MS,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const topTeamsThisMonth = useMemo(() => {
    return (summaryData?.team_performance ?? [])
      .map((row) => ({
        team_name: row.team_name,
        market_center: row.market_center,
        total_transactions: Number(row.total_transactions ?? 0),
        total_gci: Number(row.total_gci ?? 0),
      }))
      .sort((a, b) => b.total_gci - a.total_gci)
      .slice(0, 10);
  }, [summaryData?.team_performance]);

  const topIndividualAssociatesThisMonth = useMemo(() => {
    return (summaryData?.associate_performance ?? [])
      .filter((row) => {
        const name = (row.associate_name ?? '').trim().toLowerCase();
        if (!name) return false;
        if (name.startsWith('team ') || name.includes('& team')) return false;
        const teamName = String(row.team_name ?? '').trim().toLowerCase();
        if (teamName && teamName !== 'no team') return false;

        return true;
      })
      .map((row) => ({
        associate_name: row.associate_name,
        team_name: row.team_name,
        market_center: row.market_center,
        total_transactions: Number(row.total_transactions ?? 0),
        total_gci: Number(row.total_gci ?? 0),
      }))
      .sort((a, b) => b.total_gci - a.total_gci)
      .slice(0, 10);
  }, [summaryData?.associate_performance]);

  const parityTopMarketCenters = (monthEndParityData?.rows ?? [])
    .filter((row) => {
      if (isRegionalAdmin || !activeOfficeNorm) return true;
      return normalizeForMatch(row.market_center_name) === activeOfficeNorm;
    })
    .slice(0, 10);
  const parityContracts = monthEndParityData?.totals?.contracts;
  const parityTotalGci = monthEndParityData?.totals?.total_gci;
  const mtdTransactionsValue = summaryData?.mtd_registered_active.total_transactions ?? 0;
  const mtdSalesValue = summaryData?.mtd_registered_active.total_sales_value ?? 0;
  const mtdGciValue = summaryData?.mtd_registered_active.total_net_commission ?? 0;
  const mtdAverageSplitValue = summaryData?.mtd_registered_active.average_split_percentage ?? 0;
  const adminContractsValue = parityContracts ?? mtdTransactionsValue;
  const adminTotalGciValue = parityTotalGci ?? mtdGciValue;

  const { data: agentOptionsData } = useQuery({
    queryKey: ['agent-options', activeContextId],
    queryFn: () =>
      fetch('/api/agents/options', { headers: authHeaders }).then(async (r) => {
        if (!r.ok) throw new Error('Unable to load agent options');
        return r.json() as Promise<AgentOptionsResponse>;
      }),
  });

  const { refetch: refetchNextNumber } = useQuery({
    queryKey: ['transactions-next-number', activeContextId],
    queryFn: () =>
      fetch('/api/transactions/next-number', { headers: authHeaders }).then(async (r) => {
        if (!r.ok) throw new Error('Unable to fetch next transaction number');
        return r.json() as Promise<{ next_transaction_number: string }>;
      }),
    enabled: false,
  });

  const { refetch: refetchNextPocketListingNumber, isFetching: isGeneratingPocketListingNumber } = useQuery({
    queryKey: ['transactions-next-pocket-listing-number', activeContextId],
    queryFn: async ({ signal }) => {
      let response = await fetchWithTimeout('/api/transactions/pocket-listing-number/next', {
        method: 'POST',
        headers: authHeaders,
        signal,
      });
      if (response.status === 404) {
        response = await fetchWithTimeout('/api/transactions/next-pocket-listing-number', {
          method: 'POST',
          headers: authHeaders,
          signal,
        });
      }
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(payload.error ?? 'Unable to generate pocket listing number');
      }
      return response.json() as Promise<{ pocket_listing_number: string }>;
    },
    enabled: false,
  });

  const { data: listingSearchData } = useQuery({
    queryKey: ['listing-search', activeContextId, listingSearchQuery],
    queryFn: () =>
      fetch(`/api/listings/search?q=${encodeURIComponent(listingSearchQuery)}`).then(r => r.json() as Promise<ListingSearchResponse>),
    enabled: listingSearchQuery.length >= 2,
    staleTime: 30000,
  });

  const {
    data: editDocumentsData,
    isLoading: isEditDocumentsLoading,
    refetch: refetchEditDocuments,
  } = useQuery({
    queryKey: ['transaction-edit-documents', activeContextId, editingId],
    queryFn: () =>
      fetch(`/api/transactions/${editingId}/documents`, { headers: authHeaders }).then(async (r) => {
        if (!r.ok) throw new Error('Unable to load transaction documents');
        return r.json() as Promise<{ items: TransactionDocumentRow[] }>;
      }),
    enabled: Boolean(editingId) && editTab === 'documents',
  });

  const {
    data: quickSummaryData,
    isLoading: isQuickSummaryLoading,
  } = useQuery({
    queryKey: ['transaction-quick-summary', activeContextId, quickSummaryRow?.id],
    queryFn: async ({ signal }) => {
      const response = await fetchWithTimeout(`/api/transactions/${quickSummaryRow!.id}/calculated-summary`, {
        headers: authHeaders,
        signal,
        cache: 'no-store',
      });
      if (!response.ok) throw new Error('Unable to load quick summary');
      return response.json() as Promise<CalculatedSummaryResponse>;
    },
    enabled: Boolean(quickSummaryRow?.id),
    retry: 1,
  });

  const {
    data: editSummaryData,
    isLoading: isEditSummaryLoading,
    refetch: refetchEditSummary,
  } = useQuery({
    queryKey: ['transaction-edit-summary', activeContextId, editingId],
    queryFn: async ({ signal }) => {
      const response = await fetchWithTimeout(`/api/transactions/${editingId}/calculated-summary`, {
        headers: authHeaders,
        signal,
        cache: 'no-store',
      });
      if (!response.ok) throw new Error('Unable to load transaction summary');
      return response.json() as Promise<CalculatedSummaryResponse>;
    },
    enabled: Boolean(editingId) && editTab === 'summary',
    retry: 1,
  });

  const previewAssociateIds = useMemo(() => {
    const bySourceAssociateId = new Map((agentOptionsData?.items ?? []).map((item) => [item.source_associate_id, item]));
    return Array.from(new Set(
      form.agents
        .map((agent) => bySourceAssociateId.get(agent.source_associate_id)?.id ?? null)
        .filter((value): value is string => Boolean(value))
    ));
  }, [agentOptionsData?.items, form.agents]);

  const { data: previewCapRemainingData } = useQuery({
    queryKey: ['transaction-preview-cap-remaining', activeContextId, previewAssociateIds.join(',')],
    queryFn: async ({ signal }) => {
      const response = await fetchWithTimeout('/api/transactions/preview-cap-remaining', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ associate_ids: previewAssociateIds }),
        signal,
      });
      if (!response.ok) {
        throw new Error('Unable to load preview cap remaining');
      }
      return response.json() as Promise<CapRemainingPreviewResponse>;
    },
    enabled: previewAssociateIds.length > 0,
    retry: 1,
  });

  const totalPages = useMemo(() => {
    const total = data?.total ?? 0;
    return Math.max(1, Math.ceil(total / pageSize));
  }, [data?.total, pageSize]);

  const shownStart = data?.total ? ((page - 1) * pageSize) + 1 : 0;
  const shownEnd = data?.total ? Math.min(page * pageSize, data.total) : 0;

  const activeRegisterFilterLabels = useMemo(() => {
    const labels: string[] = [];
    if (search.trim()) labels.push(`Search: ${search.trim()}`);
    if (status.trim()) labels.push(`Status: ${status.trim()}`);
    if (typeFilter.trim()) labels.push(`Type: ${typeFilter.trim()}`);
    if (isTransactionsUxEnhanced && agentQuickFilter.trim()) labels.push(`Agent: ${agentQuickFilter.trim()}`);
    if (isTransactionsUxEnhanced && marketCenterQuickFilter.trim()) labels.push(`Market Centre: ${marketCenterQuickFilter.trim()}`);
    if (registerDateFilter) {
      const basis = registerDateFilter === 'transaction_date' ? 'Transaction Date' : 'Status Change Date';
      const fromLabel = registerDateFrom || 'Any';
      const toLabel = registerDateTo || 'Any';
      labels.push(`${basis}: ${fromLabel} to ${toLabel}`);
    }
    return labels;
  }, [
    agentQuickFilter,
    isTransactionsUxEnhanced,
    marketCenterQuickFilter,
    registerDateFilter,
    registerDateFrom,
    registerDateTo,
    search,
    status,
    typeFilter,
  ]);

  function clearRegisterFilters(): void {
    setSearch('');
    setStatus('');
    setTypeFilter('');
    setAgentQuickFilter('');
    setMarketCenterQuickFilter('');
    setRegisterDateFilter('');
    setRegisterDateFrom('');
    setRegisterDateTo('');
    setPage(1);
  }

  async function copyTransactionReference(item: TransactionRow): Promise<void> {
    const value = (item.transaction_number ?? item.source_transaction_id ?? '').trim();
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const area = document.createElement('textarea');
        area.value = value;
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.focus();
        area.select();
        document.execCommand('copy');
        document.body.removeChild(area);
      }
      setCopiedTransactionId(item.id);
      window.setTimeout(() => setCopiedTransactionId((current) => (current === item.id ? null : current)), 1200);
    } catch {
      setCopiedTransactionId(null);
    }
  }

  const filteredItems = useMemo(() => {
    return data?.items ?? [];
  }, [data?.items]);

  const isRentalsCategory = categoryView === 'rentals';

  const sortedItems = useMemo(() => {
    const rows = [...filteredItems];

    const compareText = (left: string | null | undefined, right: string | null | undefined): number =>
      (left ?? '').localeCompare(right ?? '', undefined, { sensitivity: 'base', numeric: true });

    const compareDate = (left: string | null | undefined, right: string | null | undefined): number => {
      const leftTime = left ? new Date(left).getTime() : Number.NEGATIVE_INFINITY;
      const rightTime = right ? new Date(right).getTime() : Number.NEGATIVE_INFINITY;
      return leftTime - rightTime;
    };

    const compareNumber = (left: string | null | undefined, right: string | null | undefined): number =>
      toNumberOrZero(left) - toNumberOrZero(right);

    rows.sort((a, b) => {
      let result = 0;
      switch (registerSortKey) {
        case 'transaction':
          result = compareText(a.transaction_number ?? a.source_transaction_id, b.transaction_number ?? b.source_transaction_id);
          break;
        case 'associate':
          result = compareText(
            a.agents.length > 0 ? getTransactionAgentDisplayName(a.agents[0]) : '',
            b.agents.length > 0 ? getTransactionAgentDisplayName(b.agents[0]) : ''
          );
          break;
        case 'market_center':
          result = compareText(a.market_center_name, b.market_center_name);
          break;
        case 'type':
          result = compareText(getDisplayTransactionType(a), getDisplayTransactionType(b));
          break;
        case 'status':
          result = compareText(a.transaction_status, b.transaction_status);
          break;
        case 'created_date':
          result = compareDate(a.transaction_date ?? a.created_at, b.transaction_date ?? b.created_at);
          break;
        case 'status_change_date':
          result = compareDate(a.status_change_date, b.status_change_date);
          break;
        case 'sales_price':
          result = compareNumber(a.sales_price, b.sales_price);
          break;
        case 'net_comm':
          result = compareNumber(a.net_comm, b.net_comm);
          break;
      }

      if (result === 0) {
        result = compareText(a.id, b.id);
      }

      return registerSortDirection === 'asc' ? result : -result;
    });

    return rows;
  }, [filteredItems, registerSortDirection, registerSortKey]);

  const canGoPrev = page > 1;
  const canGoNext = page < totalPages;
  const periodLabel = summaryData?.reporting_window
    ? `${new Date(summaryData.reporting_window.start_date).toLocaleDateString()} - ${new Date(summaryData.reporting_window.end_date).toLocaleDateString()}`
    : (() => {
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${monthStart.toLocaleDateString()} - ${now.toLocaleDateString()}`;
      })();
  const performanceBasis = summaryData?.performance_basis ?? summaryData?.reporting_window?.basis ?? 'registered';
  const performanceLabel =
    performanceBasis === 'allStatuses'
      ? 'All Statuses This Month (Month End parity)'
      : 'Registered MTD (Month End parity)';
  const summaryWorkflowCues = isTransactionsUxEnhanced
    ? (isRegionalAdmin
      ? [
          {
            title: 'Regional Workflow',
            detail: 'Use this summary to identify top and lagging market centres, then jump to Month End for submission readiness.',
          },
          {
            title: 'Decision Gate',
            detail: 'When totals look off, compare Registered MTD vs All Statuses and verify status-change timing before escalation.',
          },
        ]
      : isOfficeAdmin
        ? [
            {
              title: 'Office Workflow',
              detail: 'Track your office team and associate rankings here, then drill into Transaction Register to clear blockers quickly.',
            },
            {
              title: 'Daily Hygiene',
              detail: 'Prioritize records still in Start or Working that should already be Registered this month.',
            },
          ]
        : [])
    : [];
  const monthEndWorkflowCues = isTransactionsUxEnhanced && isRegionalAdmin
    ? [
        {
          title: 'Run Test Batch First',
          detail: 'Validate readiness, then decide between submitting ready items immediately or clearing high-impact errors first.',
        },
        {
          title: 'Submission Readiness',
          detail: monthEndPreview
            ? `${monthEndPreview.ready_to_submit.toLocaleString()} ready out of ${monthEndPreview.total_candidates.toLocaleString()} previewed.`
            : 'No preview yet. Run Test Batch to populate ready/error counts before submit.',
        },
      ]
    : [];

  function toInputDate(value: string | null): string {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  }

  function openCreateForm(): void {
    const today = new Date().toISOString().slice(0, 10);
    const threeMonths = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    setEditingId(null);
    setDirectoryDecisions({});
    setPartyValidationFields([]);
    setForceSummaryPreview(false);
    setSummaryPreviewNotice(null);
    setFormError(null);
    setListingSearchQuery('');
    setListingSearchOpen(false);
    setDocumentActionError(null);
    setDocumentTypeInput('');
    setStagedDocuments([]);
    setForm({
      source_transaction_id: '',
      transaction_number: '',
      transaction_status: 'Start',
      status_change_date: today,
      transaction_type: '',
      source_listing_id: '',
      listing_number: '',
      address: '',
      suburb: '',
      city: '',
      sales_price: '',
      list_price: '',
      net_comm: '',
      total_gci: '',
      buyer: '',
      seller: '',
      transfer_attorney: '',
      transaction_financing_type: '',
      buyer_contacts: [buildEmptyPartyContact()],
      seller_contacts: [buildEmptyPartyContact()],
      transfer_attorney_contact: buildEmptyPartyContact(),
      bond_attorney_contact: buildEmptyPartyContact(),
      bond_originator_contact: buildEmptyPartyContact(),
      bond_attorney: '',
      bond_originator: '',
      all_parties_invoiced: '',
      transaction_date: today,
      expected_date: threeMonths,
      agents: [],
    });
    setIsFormOpen(true);
    setEditTab('details');
    void refetchNextNumber().then((result) => {
      const next = result.data?.next_transaction_number;
      if (next) {
        setForm((prev) => ({ ...prev, transaction_number: next }));
      }
    });
  }

  function openEditForm(item: TransactionRow): void {
    if (!getSalesStatusEditPolicy(!isRentalTransactionItem(item), item.transaction_status, isRegionalAdmin).canOpenEditor) return;
    setEditingId(item.id);
    setDirectoryDecisions({});
    setPartyValidationFields([]);
    setForceSummaryPreview(false);
    setSummaryPreviewNotice(null);
    setFormError(null);
    setListingSearchQuery(item.listing_number ?? '');
    setListingSearchOpen(false);
    setDocumentActionError(null);
    setDocumentTypeInput('');
    setStagedDocuments([]);
    setForm({
      source_transaction_id: item.source_transaction_id,
      transaction_number: item.transaction_number ?? '',
      transaction_status: item.transaction_status ?? '',
      status_change_date: toInputDate(item.status_change_date),
      transaction_type: item.transaction_type ?? (getDisplayTransactionType(item) === '-' ? '' : getDisplayTransactionType(item)),
      source_listing_id: item.source_listing_id ?? '',
      listing_number: item.listing_number ?? '',
      address: item.address ?? '',
      suburb: item.suburb ?? '',
      city: item.city ?? '',
      sales_price: item.sales_price ?? '',
      list_price: item.list_price ?? '',
      net_comm: item.net_comm ?? '',
      total_gci: item.total_gci ?? '',
      buyer: item.buyer ?? '',
      seller: item.seller ?? '',
      transfer_attorney: item.transfer_attorney ?? '',
      transaction_financing_type: normalizeFinancingType(item.transaction_financing_type),
      buyer_contacts: normalizePartyContactList(item.party_contact_details?.buyer_contacts ?? item.party_contact_details?.buyer, item.buyer),
      seller_contacts: normalizePartyContactList(item.party_contact_details?.seller_contacts ?? item.party_contact_details?.seller, item.seller),
      transfer_attorney_contact: normalizePartyContact(item.party_contact_details?.transfer_attorney),
      bond_attorney_contact: normalizePartyContact(item.party_contact_details?.bond_attorney),
      bond_originator_contact: normalizePartyContact(item.party_contact_details?.bond_originator),
      bond_attorney: item.bond_attorney ?? '',
      bond_originator: item.bond_originator ?? '',
      all_parties_invoiced: item.all_parties_invoiced ?? '',
      transaction_date: toInputDate(item.transaction_date),
      expected_date: toInputDate(item.expected_date),
      agents: (item.agents ?? []).map((a) => ({
        ...(function buildAgentWithOutsideFallback() {
          const displayName = getTransactionAgentDisplayName(a);
          const parsedName = splitNameParts(displayName === '-' ? '' : displayName);
          return {
            source_associate_id: a.source_associate_id ?? '',
            associate_name: displayName === '-' ? '' : displayName,
            agent_role: normalizeAgentRoleForForm(a.agent_role ?? a.summary?.transaction_type ?? item.transaction_type ?? ''),
            split_percentage: a.split_percentage ? String(a.split_percentage) : '',
            outside_agency: {
              first_name: a.outside_agency?.first_name ?? parsedName.first,
              last_name: a.outside_agency?.last_name ?? parsedName.last,
              email: a.outside_agency?.email ?? '',
              phone: a.outside_agency?.phone ?? '',
              agency_name: a.outside_agency?.agency_name ?? '',
            },
          };
        })(),
      })),
    });
    setIsFormOpen(true);
    setEditTab('details');
  }

  useEffect(() => {
    const editValue = new URLSearchParams(location.search).get('edit');
    const target = editValue ? editValue.trim() : '';
    if (!target) return;

    let cancelled = false;

    const openFromDeepLink = async () => {
      const existing = (data?.items ?? []).find((item) =>
        item.id === target
        || item.source_transaction_id === target
        || (item.transaction_number ?? '') === target,
      );

      if (existing) {
        openEditForm(existing);
      } else {
        try {
          const response = await fetch(`/api/transactions/${encodeURIComponent(target)}`, { headers: authHeaders });
          if (!response.ok) return;
          const item = await response.json() as TransactionRow;
          if (cancelled) return;
          openEditForm(item);
        } catch {
          return;
        }
      }

      if (cancelled) return;
      const params = new URLSearchParams(location.search);
      params.delete('edit');
      void navigate(
        {
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : '',
        },
        { replace: true },
      );
    };

    void openFromDeepLink();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, navigate, data?.items]);

  async function uploadTransactionDocumentToId(transactionId: string, file: File, documentType: string): Promise<void> {
    const payload = new FormData();
    payload.append('document', file);
    payload.append('transaction_document_type', documentType.trim() || 'OTHER');

    const response = await fetch(`/api/transactions/${transactionId}/documents/upload`, {
      method: 'POST',
      headers: authHeaders,
      body: payload,
    });

    if (!response.ok) {
      const json = (await response.json().catch(() => ({}))) as { error?: string };
      throw new Error(json.error ?? `Failed to upload ${file.name}`);
    }
  }

  async function recalculateSavedTransactionSummary(
    transactionId: string,
    successNotice: string,
    options?: {
      showBusy?: boolean;
      timeoutMs?: number;
      failSilently?: boolean;
    }
  ): Promise<boolean> {
    const refreshPostRecalcViews = async (): Promise<void> => {
      const refreshTasks: Array<Promise<unknown>> = [refetch()];

      // Summary endpoints are expensive; only refresh them when the summary UI is active.
      if (view === 'summary' && categoryView !== 'rentals') {
        refreshTasks.push(refetchSummary());
      }

      if (Boolean(editingId) && editTab === 'summary') {
        refreshTasks.push(refetchEditSummary());
      }

      await Promise.all(refreshTasks);
    };

    const refreshAfterQueuedRecalc = async (attempts = 8, delayMs = 1500): Promise<void> => {
      for (let i = 0; i < attempts; i += 1) {
        await new Promise<void>((resolve) => {
          window.setTimeout(() => resolve(), delayMs);
        });
        await refreshPostRecalcViews();
      }
    };

    const showBusy = options?.showBusy !== false;
    const timeoutMs = options?.timeoutMs ?? 90000;
    if (showBusy) {
      setIsRecalculatingSummary(true);
    }
    try {
      const response = await fetchWithTimeout(
        `/api/transactions/${transactionId}/recalculate?mode=immediate`,
        {
          method: 'POST',
          headers: authHeaders,
          cache: 'no-store',
        },
        timeoutMs
      );
      const payload = (await response.json().catch(() => ({}))) as { error?: string; queued?: boolean };
      if (!response.ok) {
        throw new Error(payload.error ?? 'Failed to recalculate saved transaction summary');
      }

      setForceSummaryPreview(false);
      setSummaryPreviewNotice(
        payload.queued
          ? 'Recalculation queued. Summary will refresh shortly using backend cap progression.'
          : successNotice
      );
      await refreshPostRecalcViews();
      if (payload.queued) {
        void refreshAfterQueuedRecalc().catch(() => {
          // Best-effort background refresh only.
        });
      }
      return true;
    } catch (error) {
      if (options?.failSilently) {
        setSummaryPreviewNotice('Transaction saved. Recalculation is taking longer than expected; data will refresh shortly.');
        await refreshPostRecalcViews();
        return false;
      }
      throw error;
    } finally {
      if (showBusy) {
        setIsRecalculatingSummary(false);
      }
    }
  }

  async function saveTransaction(): Promise<void> {
    setIsSaving(true);
    setFormError(null);
    setPartyValidationFields([]);
    try {
      if (Object.values(directoryDecisions).some(Boolean)) {
        setEditTab('parties');
        throw new Error('Choose whether to save each new professional to the directory or this transaction only.');
      }
      if (salesStatusEditPolicy.registeredLocked && (!salesStatusEditPolicy.canRevert || form.transaction_status.trim().toLowerCase() === 'registered')) {
        throw new Error('Registered Sales are locked. A Regional Admin must revert the transaction before it can be edited.');
      }
      const isCreating = !editingId;
      const computedAfterFeesExclVat = toDecimalText(Math.max(toNumberOrZero(form.total_gci), 0) * 0.92);
      const effectiveNetCommission = isEditingRentalTransaction
        ? form.net_comm
        : computedAfterFeesExclVat;
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId ? `/api/transactions/${editingId}` : '/api/transactions';
      const buyerContacts = sanitizePartyContacts(form.buyer_contacts);
      const sellerContacts = sanitizePartyContacts(form.seller_contacts);
      if (shouldValidatePartyForm({ isSales: isSalesForm, isCreating, previousStatus: currentEditingRow?.transaction_status, nextStatus: form.transaction_status })) {
        const missingFields = validatePartyForm({
          buyerContacts: form.buyer_contacts,
          sellerContacts: form.seller_contacts,
          transferAttorney: form.transfer_attorney_contact,
          bondAttorney: form.bond_attorney_contact,
          bondOriginator: form.bond_originator_contact,
          financingType: form.transaction_financing_type,
        });
        if (missingFields.length > 0) {
          setPartyValidationFields(missingFields);
          return;
        }
      }
      const primaryBuyer = buyerContacts[0];
      const primarySeller = sellerContacts[0];
      const buyerName = buyerContacts.map((contact, idx) => compactPartyContactName(contact, `Buyer ${idx + 1}`)).join(', ');
      const sellerName = sellerContacts.map((contact, idx) => compactPartyContactName(contact, `Seller ${idx + 1}`)).join(', ');
      const transferAttorneyName = joinContactName(form.transfer_attorney_contact.first_name, form.transfer_attorney_contact.last_name);
      const bondAttorneyName = joinContactName(form.bond_attorney_contact.first_name, form.bond_attorney_contact.last_name);
      const bondOriginatorName = joinContactName(form.bond_originator_contact.first_name, form.bond_originator_contact.last_name);
      const partyContactDetails = {
        buyer: primaryBuyer,
        seller: primarySeller,
        buyer_contacts: buyerContacts,
        seller_contacts: sellerContacts,
        transfer_attorney: form.transfer_attorney_contact,
        bond_attorney: form.bond_attorney_contact,
        bond_originator: form.bond_originator_contact,
      };
      const normalizedAgents = form.agents
        .filter((a) => {
          if (a.source_associate_id) return true;
          if (isOutsideAgentRole(a.agent_role)) {
            return Boolean(
              a.outside_agency.first_name.trim()
              || a.outside_agency.last_name.trim()
              || a.outside_agency.agency_name.trim()
            );
          }
          return false;
        })
        .map((a) => ({
          ...a,
          agent_role: normalizeAgentRoleForForm(a.agent_role),
          associate_name: getOutsideAgentDisplayName(a),
        }));
      const basePayload = {
        ...form,
        net_comm: effectiveNetCommission,
        transaction_type: derivedTransactionType || form.transaction_type || null,
        status_change_date: form.status_change_date || null,
        buyer: form.buyer || buyerName,
        seller: form.seller || sellerName,
        transfer_attorney: form.transfer_attorney || transferAttorneyName || form.transfer_attorney_contact.company_name,
        ta_mobile_phone: form.transfer_attorney_contact.contact_number,
        ta_email: form.transfer_attorney_contact.email_address,
        bond_attorney: form.bond_attorney || bondAttorneyName || form.bond_attorney_contact.company_name,
        ba_mobile_phone: form.bond_attorney_contact.contact_number,
        ba_email: form.bond_attorney_contact.email_address,
        bond_originator: form.bond_originator || bondOriginatorName || form.bond_originator_contact.company_name,
        party_contact_details: partyContactDetails,
        buyer_contacts: buyerContacts,
        seller_contacts: sellerContacts,
        buyer_first_name: primaryBuyer.first_name,
        buyer_last_name: primaryBuyer.last_name,
        buyer_contact_number: primaryBuyer.contact_number,
        buyer_email_address: primaryBuyer.email_address,
        seller_first_name: primarySeller.first_name,
        seller_last_name: primarySeller.last_name,
        seller_contact_number: primarySeller.contact_number,
        seller_email_address: primarySeller.email_address,
        transfer_attorney_first_name: form.transfer_attorney_contact.first_name,
        transfer_attorney_last_name: form.transfer_attorney_contact.last_name,
        transfer_attorney_contact_number: form.transfer_attorney_contact.contact_number,
        transfer_attorney_email_address: form.transfer_attorney_contact.email_address,
        transfer_attorney_company_name: form.transfer_attorney_contact.company_name,
        bond_attorney_first_name: form.bond_attorney_contact.first_name,
        bond_attorney_last_name: form.bond_attorney_contact.last_name,
        bond_attorney_contact_number: form.bond_attorney_contact.contact_number,
        bond_attorney_email_address: form.bond_attorney_contact.email_address,
        bond_attorney_company_name: form.bond_attorney_contact.company_name,
        bond_originator_first_name: form.bond_originator_contact.first_name,
        bond_originator_last_name: form.bond_originator_contact.last_name,
        bond_originator_contact_number: form.bond_originator_contact.contact_number,
        bond_originator_email_address: form.bond_originator_contact.email_address,
        bond_originator_company_name: form.bond_originator_contact.company_name,
      };
      const requestPayload = (!isCreating && normalizedAgents.length === 0)
        ? basePayload
        : { ...basePayload, agents: normalizedAgents };
      const response = await fetchWithTimeout(url, {
        method,
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify(requestPayload),
      }, 45000);
      const json = (await response.json().catch(() => ({}))) as { id?: string; error?: string; warning?: string; code?: string; missingFields?: MissingPartyField[] };
      if (!response.ok) {
        if (json.code === 'TRANSACTION_PARTIES_INCOMPLETE' && Array.isArray(json.missingFields)) {
          setPartyValidationFields(json.missingFields);
          return;
        }
        throw new Error(json.error ?? 'Failed to save transaction');
      }

      const savedTransactionId = String(editingId ?? json.id ?? '');
      if (savedTransactionId) {
        updateVisibleTransactionCache(savedTransactionId, {
          transaction_status: form.transaction_status || undefined,
          status_change_date: form.status_change_date || undefined,
          net_comm: effectiveNetCommission || undefined,
        });
      }
      if (isCreating && savedTransactionId && stagedDocuments.length > 0) {
        const uploadResults = await Promise.allSettled(
          stagedDocuments.map((doc) => uploadTransactionDocumentToId(savedTransactionId, doc.file, doc.transaction_document_type))
        );
        const failedFiles = uploadResults
          .map((result, index) => ({ result, fileName: stagedDocuments[index]?.file.name ?? 'Unknown file' }))
          .filter((entry) => entry.result.status === 'rejected')
          .map((entry) => entry.fileName);

        if (failedFiles.length > 0) {
          setEditingId(savedTransactionId);
          setEditTab('documents');
          setStagedDocuments([]);
          setFormError(`Transaction saved, but document upload failed for: ${failedFiles.join(', ')}`);
          await refreshTransactionViews();
          return;
        }

        setStagedDocuments([]);
      }

      setIsFormOpen(false);
      await refreshTransactionViews();

      if (savedTransactionId) {
        void recalculateSavedTransactionSummary(
          savedTransactionId,
          'Saved and recalculated using backend rules. This summary now reflects persisted values.',
          { showBusy: false, timeoutMs: 12000, failSilently: true }
        );
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        const confirmed = editingId ? await confirmSavedTransactionState(editingId) : false;
        if (confirmed && editingId) {
          setIsFormOpen(false);
          await refreshTransactionViews();
          void recalculateSavedTransactionSummary(
            editingId,
            'Saved and recalculated using backend rules. This summary now reflects persisted values.',
            { showBusy: false, timeoutMs: 12000, failSilently: true }
          );
          return;
        }

        setFormError('Save request timed out. Please retry; if this keeps happening, refresh and try again.');
      } else {
        setFormError(error instanceof Error ? error.message : 'Failed to save transaction');
      }
    } finally {
      setIsSaving(false);
    }
  }

  async function uploadTransactionDocument(file: File): Promise<void> {
    if (!editingId) return;
    setIsUploadingDocument(true);
    setDocumentActionError(null);

    try {
      await uploadTransactionDocumentToId(editingId, file, documentTypeInput);

      await refetchEditDocuments();
    } catch (error) {
      setDocumentActionError(error instanceof Error ? error.message : 'Failed to upload document');
    } finally {
      setIsUploadingDocument(false);
    }
  }

  async function confirmSavedTransactionState(transactionId: string): Promise<boolean> {
    try {
      const response = await fetchWithTimeout(`/api/transactions/${transactionId}`, {
        headers: authHeaders,
        cache: 'no-store',
      }, 15000);

      if (!response.ok) {
        return false;
      }

      const detail = await response.json() as {
        id?: string;
        transaction_status?: string | null;
        transaction_type?: string | null;
        listing_number?: string | null;
        source_listing_id?: string | null;
        address?: string | null;
        suburb?: string | null;
        city?: string | null;
        sales_price?: string | null;
        list_price?: string | null;
        net_comm?: string | null;
        total_gci?: string | null;
        transaction_date?: string | null;
        status_change_date?: string | null;
      };

      if (!detail.id) {
        return false;
      }

      updateVisibleTransactionCache(transactionId, {
        transaction_status: detail.transaction_status ?? undefined,
        transaction_type: detail.transaction_type ?? undefined,
        listing_number: detail.listing_number ?? undefined,
        source_listing_id: detail.source_listing_id ?? undefined,
        address: detail.address ?? undefined,
        suburb: detail.suburb ?? undefined,
        city: detail.city ?? undefined,
        sales_price: detail.sales_price ?? undefined,
        list_price: detail.list_price ?? undefined,
        net_comm: detail.net_comm ?? undefined,
        total_gci: detail.total_gci ?? undefined,
        transaction_date: detail.transaction_date ?? undefined,
        status_change_date: detail.status_change_date ?? undefined,
      });

      return true;
    } catch {
      return false;
    }
  }

  async function deleteTransactionDocument(documentId: string): Promise<void> {
    if (!editingId) return;
    if (documentId.startsWith('staging:')) {
      setDocumentActionError('Legacy staged documents are read-only and cannot be deleted here.');
      return;
    }

    const numericId = Number(documentId);
    if (!Number.isFinite(numericId)) {
      setDocumentActionError('Invalid document id for delete operation.');
      return;
    }

    setDocumentActionError(null);
    try {
      const response = await fetch(`/api/transactions/${editingId}/documents/${numericId}`, {
        method: 'DELETE',
        headers: authHeaders,
      });

      if (!response.ok && response.status !== 204) {
        const json = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(json.error ?? 'Failed to delete document');
      }

      await refetchEditDocuments();
    } catch (error) {
      setDocumentActionError(error instanceof Error ? error.message : 'Failed to delete document');
    }
  }

  function addAgent(): void {
    setForm((prev) => {
      const newCount = prev.agents.length + 1;
      const evenSplit = (100 / newCount).toFixed(2);
      const emptyOA = buildEmptyOutsideAgency();
      const updated = prev.agents.map((a) => ({ ...a, split_percentage: evenSplit }));
      return {
        ...prev,
        agents: [...updated, { source_associate_id: '', associate_name: '', agent_role: '', split_percentage: evenSplit, outside_agency: emptyOA }],
      };
    });
  }

  function removeAgent(index: number): void {
    setForm((prev) => {
      const remaining = prev.agents.filter((_, i) => i !== index);
      if (remaining.length === 0) return { ...prev, agents: [] };
      const evenSplit = (100 / remaining.length).toFixed(2);
      return { ...prev, agents: remaining.map((a) => ({ ...a, split_percentage: evenSplit })) };
    });
  }

  function updateAgent(index: number, field: keyof TransactionFormAgent, value: string): void {
    setForm((prev) => {
      const updated = [...prev.agents];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'source_associate_id') {
        const selected = (agentOptionsData?.items ?? []).find((x) => x.source_associate_id === value);
        if (selected) {
          updated[index].associate_name = selected.full_name ?? value;
        }
      } else if (field === 'agent_role') {
        updated[index].agent_role = normalizeAgentRoleForForm(value);
        if (isOutsideAgentRole(updated[index].agent_role)) {
          updated[index].source_associate_id = '';
        }
      }
      return { ...prev, agents: updated };
    });
  }

  function updateOutsideAgency(index: number, field: keyof OutsideAgencyContact, value: string): void {
    setForm((prev) => {
      const updated = [...prev.agents];
      updated[index] = { ...updated[index], outside_agency: { ...updated[index].outside_agency, [field]: value } };
      return { ...prev, agents: updated };
    });
  }

  function selectListing(listing: ListingSearchResult): void {
    setForm((prev) => ({
      ...prev,
      source_listing_id: listing.source_listing_id,
      listing_number: listing.listing_number,
      address: listing.address ?? prev.address,
      suburb: listing.suburb ?? prev.suburb,
      city: listing.city ?? prev.city,
      list_price: listing.list_price ?? prev.list_price,
    }));
    setListingSearchQuery(listing.listing_number);
    setListingSearchOpen(false);
  }

  async function generatePocketListingNumber(): Promise<void> {
    setFormError(null);
    try {
      const result = await refetchNextPocketListingNumber();
      const pocketListingNumber = result.data?.pocket_listing_number?.trim() ?? '';
      if (!pocketListingNumber) {
        throw new Error('Unable to generate pocket listing number');
      }

      setForm((prev) => ({
        ...prev,
        listing_number: pocketListingNumber,
        source_listing_id: pocketListingNumber,
      }));
      setListingSearchQuery(pocketListingNumber);
      setListingSearchOpen(false);
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Unable to generate pocket listing number');
    }
  }

  const derivedTransactionType = getDerivedTransactionType(form.transaction_type, form.agents);
  const isPocketListingLocked = /^KWPL\d+$/i.test((form.listing_number ?? '').trim())
    || /^KWPL\d+$/i.test((form.source_listing_id ?? '').trim());
  const currentEditingRow = editingId ? (data?.items ?? []).find((x) => x.id === editingId) : null;
  const isEditingRentalTransaction = isRentalTransactionItem(currentEditingRow);
  const isSalesForm = categoryView !== 'rentals' && !isEditingRentalTransaction;
  const salesStatusEditPolicy = getSalesStatusEditPolicy(isSalesForm, currentEditingRow?.transaction_status, isRegionalAdmin);
  const registeredSaveBlocked = salesStatusEditPolicy.registeredLocked
    && (!salesStatusEditPolicy.canRevert || form.transaction_status.trim().toLowerCase() === 'registered');
  const previewCapRemainingLookup = useMemo(() => {
    const map = new Map<string, {
      cap_amount: number;
      cap_remaining: number;
      team_id: string | null;
      associate_split_pct: number;
      team_commission_split_to_team: number;
    }>();
    for (const item of previewCapRemainingData?.items ?? []) {
      map.set(item.source_associate_id, {
        cap_amount: toNumberOrZero(item.cap_amount),
        cap_remaining: toNumberOrZero(item.cap_remaining),
        team_id: item.team_id ?? null,
        associate_split_pct: toNumberOrZero(item.associate_split_pct),
        team_commission_split_to_team: toNumberOrZero(item.team_commission_split_to_team),
      });
    }
    return map;
  }, [previewCapRemainingData?.items]);
  const livePreviewRows = useMemo(
    () => buildPreviewCalculatedRows(
      form,
      editingId ?? 'new-transaction',
      currentEditingRow?.market_center_name ?? null,
      derivedTransactionType,
      previewCapRemainingLookup,
      (() => {
        const map = new Map<string, { non_mc_split_pct: number }>();
        if (!currentEditingRow) return map;
        for (const agent of currentEditingRow.agents ?? []) {
          const sourceAssociateId = (agent.source_associate_id ?? '').trim();
          if (sourceAssociateId.length === 0) continue;
          const gciAfterFees = toNumberOrZero(agent.summary?.gci_after_fees_excl_vat ?? null);
          if (gciAfterFees <= 0) continue;
          const associateDollar = toNumberOrZero(agent.summary?.associate_dollar ?? null);
          const teamDollar = toNumberOrZero(agent.summary?.team_dollar ?? null);
          const nonMcSplitPct = ((associateDollar + teamDollar) / gciAfterFees) * 100;
          if (!Number.isFinite(nonMcSplitPct)) continue;
          map.set(sourceAssociateId, { non_mc_split_pct: nonMcSplitPct });
        }
        return map;
      })(),
    ),
    [currentEditingRow, derivedTransactionType, editingId, form, previewCapRemainingLookup]
  );
  const hasLivePreviewInputs = useMemo(() => {
    if (isEditingRentalTransaction) return false;
    const hasTotalGci = toNumberOrZero(form.total_gci) > 0;
    const hasSalePrice = toNumberOrZero(form.sales_price) > 0;
    const hasListPrice = toNumberOrZero(form.list_price) > 0;
    const hasAgents = form.agents.some((agent) => {
      const hasRole = normalizeRole(agent.agent_role).length > 0;
      const hasIdentity = isOutsideAgentRole(agent.agent_role)
        ? getOutsideAgentDisplayName(agent).trim().length > 0
        : agent.source_associate_id.trim().length > 0;
      const hasSplit = toNumberOrZero(agent.split_percentage) > 0;
      return hasRole && hasIdentity && hasSplit;
    });
    const hasType = derivedTransactionType.trim().length > 0 || form.agents.some((agent) => normalizeRole(agent.agent_role).length > 0);
    return hasTotalGci && hasSalePrice && hasListPrice && hasAgents && hasType;
  }, [derivedTransactionType, form.agents, form.list_price, form.sales_price, form.total_gci, isEditingRentalTransaction]);
  const hasSummaryRelevantUnsavedChanges = useMemo(() => {
    if (!editingId || !currentEditingRow) return false;

    const toNormMoney = (value: string | null | undefined): string => {
      const n = toNumberOrZero(value);
      return Number.isFinite(n) ? n.toFixed(2) : '0.00';
    };

    const fieldChanged =
      toNormMoney(form.sales_price) !== toNormMoney(currentEditingRow.sales_price)
      || toNormMoney(form.list_price) !== toNormMoney(currentEditingRow.list_price)
      || toNormMoney(form.total_gci) !== toNormMoney(currentEditingRow.total_gci)
      || normalizeRole(form.transaction_status) !== normalizeRole(currentEditingRow.transaction_status)
      || normalizeRole(derivedTransactionType || form.transaction_type) !== normalizeRole(currentEditingRow.transaction_type ?? getDisplayTransactionType(currentEditingRow));

    if (fieldChanged) return true;

    const formAgents = form.agents
      .filter((a) => {
        if (a.source_associate_id.trim().length > 0) return true;
        return isOutsideAgentRole(a.agent_role)
          && (
            a.outside_agency.first_name.trim().length > 0
            || a.outside_agency.last_name.trim().length > 0
            || a.outside_agency.agency_name.trim().length > 0
          );
      })
      .map((a) => {
        const outsideName = getOutsideAgentDisplayName(a).trim().toLowerCase();
        return {
          key: a.source_associate_id.trim().toLowerCase() || `outside:${outsideName}`,
          role: normalizeRole(normalizeAgentRoleForForm(a.agent_role)),
          split: toNumberOrZero(a.split_percentage).toFixed(2),
        };
      })
      .sort((left, right) => left.key.localeCompare(right.key) || left.role.localeCompare(right.role));

    const currentAgents = (currentEditingRow.agents ?? [])
      .map((a) => {
        const outsideName = getTransactionAgentDisplayName(a).trim().toLowerCase();
        return {
          key: (a.source_associate_id ?? '').trim().toLowerCase() || `outside:${outsideName}`,
          role: normalizeRole(normalizeAgentRoleForForm(a.agent_role ?? a.summary?.transaction_type ?? '')),
          split: toNumberOrZero(a.split_percentage != null ? String(a.split_percentage) : null).toFixed(2),
        };
      })
      .sort((left, right) => left.key.localeCompare(right.key) || left.role.localeCompare(right.role));

    if (formAgents.length !== currentAgents.length) return true;
    for (let i = 0; i < formAgents.length; i += 1) {
      if (
        formAgents[i].key !== currentAgents[i].key
        || formAgents[i].role !== currentAgents[i].role
        || formAgents[i].split !== currentAgents[i].split
      ) {
        return true;
      }
    }

    return false;
  }, [currentEditingRow, derivedTransactionType, editingId, form.agents, form.list_price, form.sales_price, form.total_gci, form.transaction_status, form.transaction_type]);

  // Keep backend calculated-summary as source of truth for persisted transactions.
  // Use frontend live preview for new transactions or when an existing transaction has unsaved summary-relevant edits.
  const shouldUseLivePreviewRows = (!editingId || (forceSummaryPreview && hasSummaryRelevantUnsavedChanges))
    && hasLivePreviewInputs
    && livePreviewRows.length > 0;
  const snapshotEditCalculatedRows = useMemo(() => buildSnapshotCalculatedRows(currentEditingRow), [currentEditingRow]);
  const savedCalculatedSummaryRows = (editSummaryData?.items?.length ?? 0) > 0
    ? (editSummaryData?.items ?? [])
    : snapshotEditCalculatedRows;
  const calculatedSummaryRows = shouldUseLivePreviewRows
    ? livePreviewRows
    : savedCalculatedSummaryRows;
  const previewWouldChangeNumbers = useMemo(
    () => buildFinancialSignature(livePreviewRows) !== buildFinancialSignature(savedCalculatedSummaryRows),
    [livePreviewRows, savedCalculatedSummaryRows]
  );
  const snapshotQuickCalculatedRows = useMemo(() => buildSnapshotCalculatedRows(quickSummaryRow), [quickSummaryRow]);
  const quickSummaryCalculatedRows = (quickSummaryData?.items?.length ?? 0) > 0
    ? (quickSummaryData?.items ?? [])
    : snapshotQuickCalculatedRows;
  const isShowingPreviewForEdit = Boolean(editingId) && shouldUseLivePreviewRows;

  const editSummaryHeaderMeta = useMemo(() => {
    const first = calculatedSummaryRows[0];
    const fallbackAgentName = form.agents[0]?.associate_name?.trim()
      || getOutsideAgentDisplayName(form.agents[0] ?? { source_associate_id: '', associate_name: '', agent_role: '', split_percentage: '', outside_agency: buildEmptyOutsideAgency() })
      || '-';
    const uniqueSides = Array.from(new Set(
      calculatedSummaryRows
        .map((row) => (row.transaction_side ?? '').trim())
        .filter((value) => value.length > 0)
    ));
    return {
      agentName: first?.agent_name ?? fallbackAgentName,
      officeName: first?.office_name ?? currentEditingRow?.market_center_name ?? '-',
      sideLabel: uniqueSides.length > 0 ? uniqueSides.join(' / ') : (derivedTransactionType || '-'),
      moreAgents: Math.max(calculatedSummaryRows.length - 1, 0),
    };
  }, [calculatedSummaryRows, currentEditingRow?.market_center_name, derivedTransactionType, form.agents]);

  const quickSummaryHeaderMeta = useMemo(() => {
    const first = quickSummaryCalculatedRows[0];
    const uniqueSides = Array.from(new Set(
      quickSummaryCalculatedRows
        .map((row) => (row.transaction_side ?? '').trim())
        .filter((value) => value.length > 0)
    ));
    return {
      agentName: first?.agent_name ?? '-',
      officeName: first?.office_name ?? quickSummaryRow?.market_center_name ?? '-',
      sideLabel: uniqueSides.length > 0 ? uniqueSides.join(' / ') : '-',
      moreAgents: Math.max(quickSummaryCalculatedRows.length - 1, 0),
    };
  }, [quickSummaryCalculatedRows, quickSummaryRow?.market_center_name]);

  const editSummaryTotals = useMemo(() => {
    return calculatedSummaryRows.reduce((acc, row) => {
      acc.gciAfterFees += toNumberOrZero(row.gci_after_fees_excl_vat);
      acc.associate += toNumberOrZero(row.associate_dollar);
      acc.capContribution += toNumberOrZero(row.cap_contribution ?? row.market_center_dollar);
      acc.marketCenter += toNumberOrZero(row.market_center_dollar);
      acc.team += toNumberOrZero(row.team_dollar);
      return acc;
    }, { gciAfterFees: 0, associate: 0, capContribution: 0, marketCenter: 0, team: 0 });
  }, [calculatedSummaryRows]);
  const quickSummaryTotals = useMemo(() => {
    return quickSummaryCalculatedRows.reduce((acc, row) => {
      acc.gciAfterFees += toNumberOrZero(row.gci_after_fees_excl_vat);
      acc.associate += toNumberOrZero(row.associate_dollar);
      acc.capContribution += toNumberOrZero(row.cap_contribution ?? row.market_center_dollar);
      acc.marketCenter += toNumberOrZero(row.market_center_dollar);
      acc.team += toNumberOrZero(row.team_dollar);
      return acc;
    }, { gciAfterFees: 0, associate: 0, capContribution: 0, marketCenter: 0, team: 0 });
  }, [quickSummaryCalculatedRows]);

  function toggleRegisterSort(key: RegisterSortKey): void {
    if (registerSortKey === key) {
      setRegisterSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    const defaultDirection: SortDirection =
      key === 'created_date' || key === 'status_change_date' || key === 'sales_price' || key === 'net_comm'
        ? 'desc'
        : 'asc';
    setRegisterSortKey(key);
    setRegisterSortDirection(defaultDirection);
  }

  function sortIndicator(key: RegisterSortKey): string {
    if (registerSortKey !== key) return '↕';
    return registerSortDirection === 'asc' ? '↑' : '↓';
  }

  const sortedMonthEndItems = useMemo(() => {
    const rows = [...(monthEndPreview?.items ?? [])];
    const compareText = (left: string | null | undefined, right: string | null | undefined): number =>
      (left ?? '').localeCompare(right ?? '', undefined, { sensitivity: 'base', numeric: true });

    const compareNumber = (left: number | null | undefined, right: number | null | undefined): number =>
      (left ?? 0) - (right ?? 0);

    const compareDate = (left: string | null | undefined, right: string | null | undefined): number => {
      const leftTime = left ? new Date(left).getTime() : Number.NEGATIVE_INFINITY;
      const rightTime = right ? new Date(right).getTime() : Number.NEGATIVE_INFINITY;
      return leftTime - rightTime;
    };

    rows.sort((left, right) => {
      let result = 0;

      switch (monthEndSortKey) {
        case 'status': {
          const rank = (value: 'ready' | 'error') => (value === 'ready' ? 0 : 1);
          result = rank(left.status) - rank(right.status);
          break;
        }
        case 'transaction':
          result = compareText(left.transaction_number ?? left.transaction_id, right.transaction_number ?? right.transaction_id);
          break;
        case 'associate':
          result = compareText(left.associate_name, right.associate_name);
          break;
        case 'market_center':
          result = compareText(left.market_center_name, right.market_center_name);
          break;
        case 'listing':
          result = compareText(left.listing_number, right.listing_number);
          break;
        case 'gci':
          result = compareNumber(left.gci_amount, right.gci_amount);
          break;
        case 'sales_price':
          result = compareNumber(left.sales_price, right.sales_price);
          break;
        case 'reporting_date':
          result = compareDate(left.effective_reporting_date, right.effective_reporting_date);
          break;
        case 'fed_status':
          result = compareText(left.last_submission_status, right.last_submission_status);
          break;
        case 'fed_at':
          result = compareDate(left.last_submitted_at, right.last_submitted_at);
          break;
      }

      if (result === 0) {
        result = compareText(left.transaction_number ?? left.transaction_id, right.transaction_number ?? right.transaction_id);
      }

      return monthEndSortDirection === 'asc' ? result : -result;
    });

    return rows;
  }, [monthEndPreview?.items, monthEndSortDirection, monthEndSortKey]);

  const monthEndSelectedSet = useMemo(() => new Set(selectedMonthEndItemIds), [selectedMonthEndItemIds]);
  const selectedMonthEndItems = useMemo(
    () => sortedMonthEndItems.filter((item) => monthEndSelectedSet.has(item.transaction_agent_id)),
    [sortedMonthEndItems, monthEndSelectedSet]
  );
  const selectedMonthEndReadyCount = useMemo(
    () => selectedMonthEndItems.filter((item) => item.status === 'ready').length,
    [selectedMonthEndItems]
  );
  const selectedMonthEndErrorCount = useMemo(
    () => selectedMonthEndItems.filter((item) => item.status === 'error').length,
    [selectedMonthEndItems]
  );
  const selectedMonthEndGciTotal = useMemo(
    () => selectedMonthEndItems.reduce((sum, item) => sum + (item.gci_amount ?? 0), 0),
    [selectedMonthEndItems]
  );
  const selectedMonthEndFeedGciTotal = useMemo(
    () => selectedMonthEndItems.filter((item) => item.status === 'ready').reduce((sum, item) => sum + (item.gci_amount ?? 0), 0),
    [selectedMonthEndItems]
  );

  function toggleMonthEndItemSelection(transactionAgentId: string): void {
    setSelectedMonthEndItemIds((current) => {
      if (current.includes(transactionAgentId)) {
        return current.filter((id) => id !== transactionAgentId);
      }
      return [...current, transactionAgentId];
    });
  }

  function selectAllVisibleMonthEndItems(): void {
    setSelectedMonthEndItemIds(sortedMonthEndItems.map((item) => item.transaction_agent_id));
  }

  function selectReadyMonthEndItems(): void {
    setSelectedMonthEndItemIds(sortedMonthEndItems.filter((item) => item.status === 'ready').map((item) => item.transaction_agent_id));
  }

  function clearMonthEndSelection(): void {
    setSelectedMonthEndItemIds([]);
  }

  function toggleMonthEndSort(key: MonthEndSortKey): void {
    if (monthEndSortKey === key) {
      setMonthEndSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    const defaultDirection: SortDirection = key === 'sales_price' || key === 'gci' || key === 'reporting_date' ? 'desc' : 'asc';
    setMonthEndSortKey(key);
    setMonthEndSortDirection(defaultDirection);
  }

  function monthEndSortIndicator(key: MonthEndSortKey): string {
    if (monthEndSortKey !== key) return '↕';
    return monthEndSortDirection === 'asc' ? '↑' : '↓';
  }

  async function runMonthEndPreview(): Promise<void> {
    setIsPreviewingMonthEnd(true);
    setMonthEndError(null);
    setMonthEndSubmission(null);
    try {
      const params = new URLSearchParams({
        date_from: monthEndDateFrom,
        date_to: monthEndDateTo,
        include_details: '1',
        limit: '400',
      });
      if (monthEndSearch.trim()) params.set('search', monthEndSearch.trim());
      if (monthEndMarketCenterFilter.trim()) params.set('market_center', monthEndMarketCenterFilter.trim());
      if (monthEndAssociateFilter.trim()) params.set('associate', monthEndAssociateFilter.trim());
      if (monthEndListingFilter.trim()) params.set('listing_number', monthEndListingFilter.trim());
      if (monthEndTransactionFilter.trim()) params.set('transaction_number', monthEndTransactionFilter.trim());
      if (monthEndPreviewStatusFilter !== 'all') params.set('preview_status', monthEndPreviewStatusFilter);
      if (monthEndFedStatusFilter !== 'all') params.set('fed_status', monthEndFedStatusFilter);
      const response = await fetchWithTimeout(`/api/frontdoor-submissions/monthly/preview?${params.toString()}`, {
        headers: authHeaders,
      });
      const payload = (await response.json().catch(() => ({}))) as MonthEndPreviewResponse & { error?: string };
      if (!response.ok) {
        throw new Error(payload.error ?? 'Unable to run FrontDoor test batch preview');
      }
      setMonthEndPreview(payload);
      setSelectedMonthEndItemIds(payload.items.filter((item) => item.status === 'ready').map((item) => item.transaction_agent_id));
    } catch (error) {
      setMonthEndError(error instanceof Error ? error.message : 'Unable to run month-end preview');
    } finally {
      setIsPreviewingMonthEnd(false);
    }
  }

  async function submitMonthEndBatch(): Promise<void> {
    setIsSubmittingMonthEnd(true);
    setMonthEndError(null);
    try {
      const response = await fetchWithTimeout('/api/frontdoor-submissions/monthly', {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          date_from: monthEndDateFrom,
          date_to: monthEndDateTo,
          limit: Math.max(monthEndPreview?.total_candidates ?? 500, 500),
          search: monthEndSearch.trim() || undefined,
          market_center: monthEndMarketCenterFilter.trim() || undefined,
          associate: monthEndAssociateFilter.trim() || undefined,
          listing_number: monthEndListingFilter.trim() || undefined,
          transaction_number: monthEndTransactionFilter.trim() || undefined,
          preview_status: monthEndPreviewStatusFilter !== 'all' ? monthEndPreviewStatusFilter : undefined,
          fed_status: monthEndFedStatusFilter !== 'all' ? monthEndFedStatusFilter : undefined,
          selected_transaction_agent_ids: selectedMonthEndItemIds,
        }),
      }, 180000);
      const payload = (await response.json().catch(() => ({}))) as MonthEndSubmitResponse & { error?: string };
      if (!response.ok) {
        throw new Error(payload.error ?? 'Unable to submit month-end FrontDoor batch');
      }
      setMonthEndSubmission(payload);
    } catch (error) {
      if (isAbortError(error)) {
        setMonthEndError('Submission is still processing in the background. Wait a moment, then run Test Batch again to refresh status.');
      } else {
        setMonthEndError(error instanceof Error ? error.message : 'Unable to submit month-end batch');
      }
    } finally {
      setIsSubmittingMonthEnd(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">Transactions</h1>
          <p className="mt-0.5 text-sm text-slate-500">Manage transaction records and monitor pipeline performance.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="status-chip info">{(data?.total ?? 0).toLocaleString()} total</span>
          {!isRentalsCategory && (
            <button className="primary-btn inline-flex items-center gap-2" type="button" onClick={openCreateForm}>
              <UiIcon kind="file" className="h-4 w-4" />
              Add Transaction
            </button>
          )}
        </div>
      </div>

      <section className="surface-card p-2">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setCategoryView('sales');
              setView('summary');
              setSearch('');
              setStatus('');
              setTypeFilter('');
              setPage(1);
            }}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${categoryView === 'sales' ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            <UiIcon kind="sales" className="h-4 w-4" />
            Sales
          </button>
          <button
            type="button"
            onClick={() => {
              setCategoryView('rentals');
              setView('register');
              setSearch('');
              setStatus('');
              setTypeFilter('');
              setPage(1);
            }}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${categoryView === 'rentals' ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            <UiIcon kind="rentals" className="h-4 w-4" />
            Rentals
          </button>

          <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden="true" />

          <button
            type="button"
            onClick={() => setView('summary')}
            disabled={isRentalsCategory}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${view === 'summary' ? 'bg-red-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'} ${isRentalsCategory ? 'cursor-not-allowed opacity-60' : ''}`}
          >
            <UiIcon kind="summary" className="h-4 w-4" />
            Summary
          </button>
          <button
            type="button"
            onClick={() => setView('register')}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${view === 'register' ? 'bg-red-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            <UiIcon kind="register" className="h-4 w-4" />
            {isRentalsCategory ? 'Rental Transactions' : 'Transaction Register'}
          </button>
          {!isRentalsCategory && isRegionalAdmin && (
            <button
              type="button"
              onClick={() => setView('month_end')}
              className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${view === 'month_end' ? 'bg-red-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              <UiIcon kind="calendar-clock" className="h-4 w-4" />
              Month End
            </button>
          )}
        </div>
      </section>

      {/* Transaction Workspace Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/60 p-2 backdrop-blur-sm md:p-4">
          <div className="mx-auto flex h-[calc(100vh-1rem)] max-w-[1800px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl md:h-[calc(100vh-2rem)]">

            {/* Modal Header */}
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between shrink-0">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Transaction Workspace</p>
                <h2 className="text-2xl font-semibold text-slate-900 flex items-center gap-3">
                  {form.transaction_number ? (
                    <span className="rounded-md bg-red-50 px-2 py-0.5 text-base font-bold text-red-700 border border-red-200">{form.transaction_number}</span>
                  ) : (
                    <span className="text-slate-400 text-base">Number auto-generated on save</span>
                  )}
                  {form.address ? ` - ${form.address}` : currentEditingRow ? 'Edit Transaction' : 'New Transaction'}
                </h2>
                {form.transaction_status && (
                  <span className="mt-0.5 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">{form.transaction_status}</span>
                )}
                {salesStatusEditPolicy.canRevert && <label className="mt-2 flex items-center gap-2 text-sm">
                  <span>Revert To</span>
                  <select className="rounded-lg border border-slate-300 px-3 py-2" value={form.transaction_status}
                    onChange={(event) => setForm((previous) => ({ ...previous, transaction_status: event.target.value, status_change_date: new Date().toISOString().slice(0, 10) }))}>
                    {TRANSACTION_STATUSES.filter((statusOption) => statusOption.toLowerCase() !== 'registered').map((statusOption) => <option key={statusOption} value={statusOption}>{statusOption}</option>)}
                  </select>
                </label>}
              </div>
              <div className="flex items-center gap-2">
                <button className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm" type="button" onClick={() => setIsFormOpen(false)}>Cancel</button>
                <button className="rounded-lg border border-slate-300 px-4 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50" type="button">
                  Feed to Frontdoor
                </button>
                <button className="primary-btn" type="button" onClick={() => void saveTransaction()} disabled={isSaving || registeredSaveBlocked || Object.values(directoryDecisions).some(Boolean)}>
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>

            {/* Body: sidebar + content */}
            <div className="flex min-h-0 flex-1">

              {/* Sidebar Navigation */}
              <aside className="w-56 border-r border-slate-200 bg-slate-50 p-3 space-y-1 shrink-0">
                {([
                  ['details', 'Transaction Details'],
                  ['parties', 'Transaction Parties'],
                  ['documents', 'Documents'],
                  ['notes', 'Notes'],
                  ['emails', 'Email History'],
                  ['summary', 'Summary'],
                ] as [TransactionEditTab, string][]).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setEditTab(key)}
                    className={`w-full rounded-lg px-3 py-2 text-left text-sm font-medium ${editTab === key ? 'bg-red-600 text-white' : 'text-slate-700 hover:bg-white'}`}
                  >
                    {label}
                  </button>
                ))}
              </aside>

              {/* Content Panel */}
              <fieldset disabled={salesStatusEditPolicy.registeredLocked} className="min-w-0 flex-1 overflow-auto p-6 space-y-6">
                {formError && <p className="text-sm text-amber-700 rounded-lg bg-amber-50 p-3 border border-amber-200">{formError}</p>}

                {editTab === 'details' && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-semibold text-slate-900">{isEditingRentalTransaction ? 'Rental Transaction Details' : 'Transaction Details'}</h3>

                    <div>
                      <h4 className="mb-3 text-sm font-semibold text-slate-700">Transaction Information</h4>
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                        <label className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-slate-600">Transaction Number</span>
                          <input className="rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm" placeholder="Auto-generated on save" value={form.transaction_number} readOnly />
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-slate-600">Transaction Status</span>
                          <select
                            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                            value={form.transaction_status}
                            onChange={(e) => setForm((p) => {
                              const nextStatus = e.target.value;
                              const today = new Date().toISOString().slice(0, 10);
                              return {
                                ...p,
                                transaction_status: nextStatus,
                                status_change_date: p.transaction_status !== nextStatus ? today : p.status_change_date,
                              };
                            })}
                          >
                            {TRANSACTION_STATUSES.map((sv) => (
                              <option key={sv} value={sv}>{sv}</option>
                            ))}
                          </select>
                        </label>
                        {isSalesForm && <label className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-slate-600">Financing Type *</span>
                          <select className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={normalizeFinancingType(form.transaction_financing_type)}
                            onChange={(event) => setForm((previous) => ({ ...previous, transaction_financing_type: event.target.value }))}>
                            <option value="">Select financing</option>
                            <option value="Cash">Cash</option>
                            <option value="Bonded">Bonded</option>
                          </select>
                        </label>}
                        <label className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-slate-600">Status Change Date</span>
                          <input
                            className={`rounded-lg border border-slate-300 px-3 py-2 text-sm ${isRegionalAdmin ? '' : 'bg-slate-50 text-slate-600'}`}
                            type="date"
                            value={form.status_change_date}
                            onChange={(e) => setForm((p) => ({ ...p, status_change_date: e.target.value }))}
                            readOnly={!isRegionalAdmin}
                            disabled={!isRegionalAdmin}
                          />
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-slate-600">Transaction Type</span>
                          <input
                            className="rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm"
                            value={derivedTransactionType}
                            readOnly
                          />
                          <span className="text-[11px] text-slate-500">
                            Derived from the assigned agent roles. If both buyer and seller roles are present, this becomes Both.
                          </span>
                        </label>
                        <div className="flex flex-col gap-1 relative lg:col-span-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-medium text-slate-600">Listing</span>
                            <button
                              type="button"
                              className="rounded-md border border-slate-300 px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => { void generatePocketListingNumber(); }}
                              disabled={isGeneratingPocketListingNumber || isPocketListingLocked}
                            >
                              {isGeneratingPocketListingNumber ? 'Generating...' : 'Pocket Listings'}
                            </button>
                          </div>
                          <input
                            className={`rounded-lg border border-slate-300 px-3 py-2 text-sm ${isPocketListingLocked ? 'bg-slate-50 text-slate-600' : ''}`}
                            placeholder="Search by listing number, address or suburb..."
                            value={isPocketListingLocked ? form.listing_number : (listingSearchQuery || form.listing_number)}
                            onChange={(e) => {
                              if (isPocketListingLocked) return;
                              setListingSearchQuery(e.target.value);
                              setListingSearchOpen(true);
                            }}
                            onFocus={() => {
                              if (isPocketListingLocked) return;
                              if (listingSearchQuery.length >= 2) setListingSearchOpen(true);
                            }}
                            onBlur={() => setTimeout(() => setListingSearchOpen(false), 150)}
                            autoComplete="off"
                            readOnly={isPocketListingLocked}
                          />
                          {listingSearchOpen && !isPocketListingLocked && (listingSearchData?.items ?? []).length > 0 && (
                            <ul className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg text-sm">
                              {(listingSearchData?.items ?? []).map((item) => (
                                <li key={item.id}>
                                  <button
                                    type="button"
                                    className="w-full px-3 py-2 text-left hover:bg-slate-50"
                                    onMouseDown={() => selectListing(item)}
                                  >
                                    <span className="font-medium text-slate-800">{item.listing_number}</span>
                                    {item.address && <span className="ml-2 text-slate-500">{item.address}{item.suburb ? `, ${item.suburb}` : ''}</span>}
                                    {item.list_price && <span className="ml-2 text-green-700 font-medium">{toMoney(item.list_price)}</span>}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                          {isPocketListingLocked && (
                            <p className="text-[11px] text-slate-500">Pocket listing number is locked once generated.</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="mb-3 text-sm font-semibold text-slate-700 border-t pt-4">Property Details</h4>
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                        <label className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-slate-600">Address</span>
                          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Address" value={form.address} onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))} />
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-slate-600">Suburb</span>
                          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Suburb" value={form.suburb} onChange={(e) => setForm((p) => ({ ...p, suburb: e.target.value }))} />
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-slate-600">City</span>
                          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="City" value={form.city} onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))} />
                        </label>
                      </div>
                    </div>

                    <div>
                      <h4 className="mb-3 text-sm font-semibold text-slate-700 border-t pt-4">Financial Details</h4>
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                        {isEditingRentalTransaction ? (
                          <label className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-slate-600">Rental Amount (Optional)</span>
                            <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Rental amount" value={form.sales_price} onChange={(e) => setForm((p) => ({ ...p, sales_price: e.target.value }))} />
                          </label>
                        ) : (
                          <>
                            <label className="flex flex-col gap-1">
                              <span className="text-xs font-medium text-slate-600">Sales Price</span>
                              <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Sales price" value={form.sales_price} onChange={(e) => setForm((p) => ({ ...p, sales_price: e.target.value }))} />
                            </label>
                            <label className="flex flex-col gap-1">
                              <span className="text-xs font-medium text-slate-600">List Price</span>
                              <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="List price" value={form.list_price} onChange={(e) => setForm((p) => ({ ...p, list_price: e.target.value }))} />
                            </label>
                          </>
                        )}
                        {isEditingRentalTransaction && (
                          <label className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-slate-600">Agent Net Amount</span>
                            <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Agent net amount" value={form.net_comm} onChange={(e) => setForm((p) => ({ ...p, net_comm: e.target.value }))} />
                          </label>
                        )}
                        <label className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-slate-600">{isEditingRentalTransaction ? 'Gross Commission' : 'Total GCI excl Vat'}</span>
                          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder={isEditingRentalTransaction ? 'Gross commission' : 'Total GCI excl Vat'} value={form.total_gci} onChange={(e) => setForm((p) => ({ ...p, total_gci: e.target.value }))} />
                        </label>
                        {!isEditingRentalTransaction && (
                          <label className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-slate-600">GCI less PR and GS</span>
                            <input
                              className="rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                              value={toDecimalText(Math.max(toNumberOrZero(form.total_gci), 0) * 0.92)}
                              readOnly
                            />
                          </label>
                        )}
                        <label className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-slate-600">Transaction Date</span>
                          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" type="date" value={form.transaction_date} onChange={(e) => setForm((p) => ({ ...p, transaction_date: e.target.value }))} />
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-slate-600">Expected Date</span>
                          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" type="date" value={form.expected_date} onChange={(e) => setForm((p) => ({ ...p, expected_date: e.target.value }))} />
                        </label>
                      </div>
                    </div>

                    <div className="border-t border-slate-200 pt-4">
                      <div className="mb-4 flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-slate-700">Transaction Agents</h4>
                        <button type="button" onClick={addAgent} className="text-xs font-medium text-blue-600 hover:text-blue-700">
                          + Add Agent
                        </button>
                      </div>
                      {form.agents.length === 0 ? (
                        <p className="text-sm text-slate-500">No agents added yet. Click + Add Agent to proceed.</p>
                      ) : (
                        <div className="space-y-3">
                          {(() => {
                            const totalSplit = form.agents.reduce((sum, a) => sum + (parseFloat(a.split_percentage) || 0), 0);
                            const splitOk = Math.abs(totalSplit - 100) < 0.05;
                            return (
                              <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${splitOk ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
                                <span>Total Split: {totalSplit.toFixed(2)}%</span>
                                {!splitOk && <span className="text-xs">Must equal 100%</span>}
                                {splitOk && <span className="text-xs">Valid</span>}
                              </div>
                            );
                          })()}
                          {form.agents.map((agent, idx) => (
                            <div key={idx} className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
                              <div className="flex items-end gap-2">
                                <label className="flex-1 flex flex-col gap-1">
                                  <span className="text-xs font-medium text-slate-600">Role</span>
                                  <select
                                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                                    value={agent.agent_role}
                                    onChange={(e) => updateAgent(idx, 'agent_role', e.target.value)}
                                  >
                                    <option value="">Select role</option>
                                    {getRoleOptions(agent.agent_role).map((r) => (
                                      <option key={r} value={r}>{r}</option>
                                    ))}
                                  </select>
                                </label>
                                {!isOutsideAgentRole(agent.agent_role) && (
                                  <label className="flex-1 flex flex-col gap-1">
                                    <span className="text-xs font-medium text-slate-600">Agent Name</span>
                                    <select
                                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                                      value={agent.source_associate_id}
                                      onChange={(e) => updateAgent(idx, 'source_associate_id', e.target.value)}
                                    >
                                      <option value="">Select agent</option>
                                      {(agentOptionsData?.items ?? []).map((opt) => (
                                        <option key={opt.source_associate_id} value={opt.source_associate_id}>
                                          {(opt.full_name ?? opt.source_associate_id) + (opt.market_center_name ? ` - ${opt.market_center_name}` : '')}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                )}
                                <label className="w-28 flex flex-col gap-1">
                                  <span className="text-xs font-medium text-slate-600">Split %</span>
                                  <input type="number" step="0.01" min="0" max="100" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="0.00" value={agent.split_percentage} onChange={(e) => updateAgent(idx, 'split_percentage', e.target.value)} />
                                </label>
                                <button type="button" onClick={() => removeAgent(idx)} className="rounded-lg bg-red-50 px-2 py-2 text-sm font-medium text-red-600 hover:bg-red-100">
                                  Remove
                                </button>
                              </div>
                              {isOutsideAgentRole(agent.agent_role) && (
                                <div className="grid grid-cols-2 gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3">
                                  <p className="col-span-2 text-xs font-semibold text-amber-900">Outside Agent Details</p>
                                  <input className="rounded-lg border border-amber-300 px-3 py-2 text-sm" placeholder="First Name" value={agent.outside_agency.first_name} onChange={(e) => updateOutsideAgency(idx, 'first_name', e.target.value)} />
                                  <input className="rounded-lg border border-amber-300 px-3 py-2 text-sm" placeholder="Last Name" value={agent.outside_agency.last_name} onChange={(e) => updateOutsideAgency(idx, 'last_name', e.target.value)} />
                                  <input className="rounded-lg border border-amber-300 px-3 py-2 text-sm" placeholder="Email" type="email" value={agent.outside_agency.email} onChange={(e) => updateOutsideAgency(idx, 'email', e.target.value)} />
                                  <input className="rounded-lg border border-amber-300 px-3 py-2 text-sm" placeholder="Phone" value={agent.outside_agency.phone} onChange={(e) => updateOutsideAgency(idx, 'phone', e.target.value)} />
                                  <input className="col-span-2 rounded-lg border border-amber-300 px-3 py-2 text-sm" placeholder="Agency Name" value={agent.outside_agency.agency_name} onChange={(e) => updateOutsideAgency(idx, 'agency_name', e.target.value)} />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {editTab === 'parties' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-slate-900">Transaction Parties</h3>
                    <div className="grid gap-4 xl:grid-cols-2">
                      <section className="rounded-xl border border-slate-200 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <h4 className="text-base font-semibold">Buyers</h4>
                          <button
                            type="button"
                            className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                            onClick={() => setForm((prev) => ({ ...prev, buyer_contacts: [...prev.buyer_contacts, buildEmptyPartyContact()] }))}
                          >
                            + Add Buyer
                          </button>
                        </div>
                        <div className="mt-3 space-y-2">
                          {form.buyer_contacts.map((contact, idx) => (
                            <details key={`buyer-${idx}`} className="rounded-lg border border-slate-200 bg-slate-50">
                              <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm font-medium text-slate-800">
                                <span>{compactPartyContactName(contact, `Buyer ${idx + 1}`)}</span>
                                {form.buyer_contacts.length > 1 && (
                                  <button
                                    type="button"
                                    className="rounded-md border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setForm((prev) => ({ ...prev, buyer_contacts: prev.buyer_contacts.filter((_, i) => i !== idx) }));
                                    }}
                                  >
                                    Remove
                                  </button>
                                )}
                              </summary>
                              <div className="grid grid-cols-1 gap-3 border-t border-slate-200 p-3 sm:grid-cols-2">
                                <label className="flex flex-col gap-1">
                                  <span className="text-xs font-medium text-slate-600">First Name{isSalesForm ? ' *' : ''}</span>
                                  <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={contact.first_name} onChange={(e) => setForm((prev) => ({ ...prev, buyer_contacts: prev.buyer_contacts.map((entry, i) => i === idx ? { ...entry, first_name: e.target.value } : entry) }))} placeholder="First name" />
                                </label>
                                <label className="flex flex-col gap-1">
                                  <span className="text-xs font-medium text-slate-600">Last Name{isSalesForm ? ' *' : ''}</span>
                                  <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={contact.last_name} onChange={(e) => setForm((prev) => ({ ...prev, buyer_contacts: prev.buyer_contacts.map((entry, i) => i === idx ? { ...entry, last_name: e.target.value } : entry) }))} placeholder="Last name" />
                                </label>
                                <label className="flex flex-col gap-1">
                                  <span className="text-xs font-medium text-slate-600">Contact Number{isSalesForm ? ' *' : ''}</span>
                                  <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={contact.contact_number} onChange={(e) => setForm((prev) => ({ ...prev, buyer_contacts: prev.buyer_contacts.map((entry, i) => i === idx ? { ...entry, contact_number: e.target.value } : entry) }))} placeholder="Contact number" />
                                </label>
                                <label className="flex flex-col gap-1">
                                  <span className="text-xs font-medium text-slate-600">Email Address{isSalesForm ? ' *' : ''}</span>
                                  <input type="email" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={contact.email_address} onChange={(e) => setForm((prev) => ({ ...prev, buyer_contacts: prev.buyer_contacts.map((entry, i) => i === idx ? { ...entry, email_address: e.target.value } : entry) }))} placeholder="Email address" />
                                </label>
                                {isSalesForm && <label className="flex flex-col gap-1 sm:col-span-2">
                                  <span className="text-xs font-medium text-slate-600">Company Name</span>
                                  <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={contact.company_name} onChange={(e) => setForm((prev) => ({ ...prev, buyer_contacts: prev.buyer_contacts.map((entry, i) => i === idx ? { ...entry, company_name: e.target.value } : entry) }))} placeholder="Company name" />
                                </label>}
                              </div>
                            </details>
                          ))}
                        </div>
                      </section>

                      <section className="rounded-xl border border-slate-200 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <h4 className="text-base font-semibold">Sellers</h4>
                          <button
                            type="button"
                            className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                            onClick={() => setForm((prev) => ({ ...prev, seller_contacts: [...prev.seller_contacts, buildEmptyPartyContact()] }))}
                          >
                            + Add Seller
                          </button>
                        </div>
                        <div className="mt-3 space-y-2">
                          {form.seller_contacts.map((contact, idx) => (
                            <details key={`seller-${idx}`} className="rounded-lg border border-slate-200 bg-slate-50">
                              <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm font-medium text-slate-800">
                                <span>{compactPartyContactName(contact, `Seller ${idx + 1}`)}</span>
                                {form.seller_contacts.length > 1 && (
                                  <button
                                    type="button"
                                    className="rounded-md border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setForm((prev) => ({ ...prev, seller_contacts: prev.seller_contacts.filter((_, i) => i !== idx) }));
                                    }}
                                  >
                                    Remove
                                  </button>
                                )}
                              </summary>
                              <div className="grid grid-cols-1 gap-3 border-t border-slate-200 p-3 sm:grid-cols-2">
                                <label className="flex flex-col gap-1">
                                  <span className="text-xs font-medium text-slate-600">First Name{isSalesForm ? ' *' : ''}</span>
                                  <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={contact.first_name} onChange={(e) => setForm((prev) => ({ ...prev, seller_contacts: prev.seller_contacts.map((entry, i) => i === idx ? { ...entry, first_name: e.target.value } : entry) }))} placeholder="First name" />
                                </label>
                                <label className="flex flex-col gap-1">
                                  <span className="text-xs font-medium text-slate-600">Last Name{isSalesForm ? ' *' : ''}</span>
                                  <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={contact.last_name} onChange={(e) => setForm((prev) => ({ ...prev, seller_contacts: prev.seller_contacts.map((entry, i) => i === idx ? { ...entry, last_name: e.target.value } : entry) }))} placeholder="Last name" />
                                </label>
                                <label className="flex flex-col gap-1">
                                  <span className="text-xs font-medium text-slate-600">Contact Number{isSalesForm ? ' *' : ''}</span>
                                  <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={contact.contact_number} onChange={(e) => setForm((prev) => ({ ...prev, seller_contacts: prev.seller_contacts.map((entry, i) => i === idx ? { ...entry, contact_number: e.target.value } : entry) }))} placeholder="Contact number" />
                                </label>
                                <label className="flex flex-col gap-1">
                                  <span className="text-xs font-medium text-slate-600">Email Address{isSalesForm ? ' *' : ''}</span>
                                  <input type="email" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={contact.email_address} onChange={(e) => setForm((prev) => ({ ...prev, seller_contacts: prev.seller_contacts.map((entry, i) => i === idx ? { ...entry, email_address: e.target.value } : entry) }))} placeholder="Email address" />
                                </label>
                                {isSalesForm && <label className="flex flex-col gap-1 sm:col-span-2">
                                  <span className="text-xs font-medium text-slate-600">Company Name</span>
                                  <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={contact.company_name} onChange={(e) => setForm((prev) => ({ ...prev, seller_contacts: prev.seller_contacts.map((entry, i) => i === idx ? { ...entry, company_name: e.target.value } : entry) }))} placeholder="Company name" />
                                </label>}
                              </div>
                            </details>
                          ))}
                        </div>
                      </section>

                      <section className="rounded-xl border border-slate-200 p-4 xl:col-span-2">
                        <h4 className="text-base font-semibold">Legal Parties</h4>
                        {isSalesForm && isRegionalAdmin && <button type="button" className="mt-2 rounded-lg border border-slate-300 px-3 py-2 text-xs" onClick={() => setDirectoryManagerOpen(true)}>Manage KWSA directory</button>}
                        <div className="mt-3 grid gap-4 lg:grid-cols-3">
                          {[
                            ['Transferring Attorney', form.transfer_attorney_contact, 'transfer_attorney_contact'] as const,
                            ['Bond Attorney', form.bond_attorney_contact, 'bond_attorney_contact'] as const,
                            ['Bond Originator', form.bond_originator_contact, 'bond_originator_contact'] as const,
                          ].map(([label, contact, key]) => {
                            const fieldPolicy = getProfessionalPartyInputPolicy(key, isSalesForm, form.transaction_financing_type);
                            const disabledInCash = fieldPolicy.disabled;
                            return <div key={key} className={`min-w-0 rounded-lg border border-slate-200 bg-slate-50 p-3 ${disabledInCash ? 'opacity-60' : ''}`}>
                              <h5 className="text-sm font-semibold text-slate-800">{label}</h5>
                              {isSalesForm && <ProfessionalDirectoryPicker key={editingId ?? 'new'}
                                category={key === 'transfer_attorney_contact' ? 'transferring_attorney' : key === 'bond_attorney_contact' ? 'bond_attorney' : 'bond_originator'}
                                contact={contact} disabled={disabledInCash || salesStatusEditPolicy.registeredLocked}
                                onChange={(selected) => setForm((previous) => ({ ...previous, [key]: selected }))}
                                onPending={(pending) => setDirectoryDecisions((previous) => previous[key] === pending ? previous : { ...previous, [key]: pending })} />}
                              <div className="mt-3 grid grid-cols-1 gap-3">
                                <label className="flex flex-col gap-1">
                                  <span className="text-xs font-medium text-slate-600">First Name{fieldPolicy.required ? ' *' : ''}</span>
                                  <input disabled={disabledInCash} className="rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-slate-100" value={contact.first_name} onChange={(e) => setForm((prev) => ({ ...prev, [key]: { ...prev[key], first_name: e.target.value } }))} placeholder="First name" />
                                </label>
                                <label className="flex flex-col gap-1">
                                  <span className="text-xs font-medium text-slate-600">Last Name{fieldPolicy.required ? ' *' : ''}</span>
                                  <input disabled={disabledInCash} className="rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-slate-100" value={contact.last_name} onChange={(e) => setForm((prev) => ({ ...prev, [key]: { ...prev[key], last_name: e.target.value } }))} placeholder="Last name" />
                                </label>
                                <label className="flex flex-col gap-1">
                                  <span className="text-xs font-medium text-slate-600">Contact Number{fieldPolicy.required ? ' *' : ''}</span>
                                  <input disabled={disabledInCash} className="rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-slate-100" value={contact.contact_number} onChange={(e) => setForm((prev) => ({ ...prev, [key]: { ...prev[key], contact_number: e.target.value } }))} placeholder="Contact number" />
                                </label>
                                <label className="flex flex-col gap-1">
                                  <span className="text-xs font-medium text-slate-600">Email Address{fieldPolicy.required ? ' *' : ''}</span>
                                  <input disabled={disabledInCash} type="email" className="rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-slate-100" value={contact.email_address} onChange={(e) => setForm((prev) => ({ ...prev, [key]: { ...prev[key], email_address: e.target.value } }))} placeholder="Email address" />
                                </label>
                                <label className="flex flex-col gap-1">
                                  <span className="text-xs font-medium text-slate-600">Company Name{fieldPolicy.required ? ' *' : ''}</span>
                                  <input disabled={disabledInCash} className="rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-slate-100" value={contact.company_name} onChange={(e) => setForm((prev) => ({ ...prev, [key]: { ...prev[key], company_name: e.target.value } }))} placeholder="Company name" />
                                </label>
                              </div>
                            </div>;
                          })}
                        </div>
                      </section>

                      <section className="rounded-xl border border-slate-200 p-4 xl:col-span-2">
                        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-end">
                          <div>
                            <h4 className="text-base font-semibold">Invoicing</h4>
                            <p className="mt-1 text-sm text-slate-500">Keep the invoicing note short and readable for the register and detail screens.</p>
                          </div>
                          <label className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-slate-600">All Parties Invoiced</span>
                            <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={form.all_parties_invoiced} onChange={(e) => setForm((prev) => ({ ...prev, all_parties_invoiced: e.target.value }))} placeholder="Yes / No / Notes" />
                          </label>
                        </div>
                      </section>

                      <section className="rounded-xl border border-slate-200 p-4 xl:col-span-2">
                        <h4 className="text-base font-semibold">Agents</h4>
                        <p className="mt-2 text-sm text-slate-500">
                          {form.agents.length > 0 ? form.agents.map((a) => getOutsideAgentDisplayName(a) || a.associate_name).filter(Boolean).join(', ') : 'No agents linked'}
                        </p>
                        <p className="mt-1 text-xs text-slate-400">Manage agent splits and roles in the Transaction Details tab.</p>
                      </section>
                    </div>
                  </div>
                )}

                {editTab === 'documents' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-slate-900">Transaction Documents</h3>
                    <div className="rounded-xl border border-slate-200 p-4">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
                        <label className="flex-1">
                          <span className="mb-1 block text-xs font-medium text-slate-600">Document Name / Type</span>
                          <input
                            type="text"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                            value={documentTypeInput}
                            onChange={(e) => setDocumentTypeInput(e.target.value)}
                            placeholder="Document name, e.g. OTP, Property Declaration, FICA"
                          />
                        </label>
                        <label className="inline-flex cursor-pointer items-center justify-center rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-400">
                          {isUploadingDocument ? 'Uploading...' : 'Upload Document'}
                          <input
                            type="file"
                            className="hidden"
                            disabled={isUploadingDocument}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (!editingId) {
                                const stagedId = `staged:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
                                setStagedDocuments((prev) => [
                                  ...prev,
                                  {
                                    id: stagedId,
                                    file,
                                    transaction_document_type: documentTypeInput.trim() || 'OTHER',
                                    created_at: new Date().toISOString(),
                                  },
                                ]);
                              } else {
                                void uploadTransactionDocument(file);
                              }
                              e.currentTarget.value = '';
                            }}
                          />
                        </label>
                      </div>
                      <p className="mt-2 text-xs text-slate-500">
                        {editingId
                          ? 'Upload directly to the transaction record.'
                          : 'Files are staged now and uploaded automatically after the transaction is saved.'}
                      </p>
                      {documentActionError && <p className="mt-2 text-sm text-red-600">{documentActionError}</p>}
                    </div>

                    <div className="rounded-xl border border-slate-200">
                      {isEditDocumentsLoading ? (
                        <p className="p-4 text-sm text-slate-500">Loading documents...</p>
                      ) : editingId ? (editDocumentsData?.items ?? []).length > 0 : stagedDocuments.length > 0 ? (
                        <div className="divide-y divide-slate-100">
                          {(editingId ? (editDocumentsData?.items ?? []) : stagedDocuments.map((doc) => ({
                            id: doc.id,
                            transaction_id: 'new',
                            source_document_id: null,
                            source_transaction_document_type_id: null,
                            transaction_document_type: doc.transaction_document_type,
                            file_name: doc.file.name,
                            document_url: null,
                            preview_url: null,
                            created_at: doc.created_at,
                            updated_at: doc.created_at,
                          }))).map((doc) => (
                            <div key={doc.id} className="flex items-center justify-between gap-3 p-4">
                              <div>
                                <p className="text-sm font-semibold text-slate-900">{doc.file_name || 'Document'}</p>
                                <p className="text-xs text-slate-500">
                                  {doc.transaction_document_type || 'Document'} • {toShortDate(doc.created_at)}
                                  {!editingId ? ' • Staged' : doc.id.startsWith('staging:') ? ' • Legacy source' : ''}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                {doc.document_url && (
                                  <a
                                    href={doc.document_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                                  >
                                    Open
                                  </a>
                                )}
                                <button
                                  type="button"
                                  disabled={editingId ? doc.id.startsWith('staging:') : false}
                                  onClick={() => {
                                    if (!editingId) {
                                      setStagedDocuments((prev) => prev.filter((entry) => entry.id !== doc.id));
                                      return;
                                    }
                                    void deleteTransactionDocument(doc.id);
                                  }}
                                  className="rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {editingId ? 'Delete' : 'Remove'}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="p-4 text-sm text-slate-500">
                          {editingId ? 'No documents attached to this transaction yet.' : 'No staged documents yet. Add files now and they will be linked after save.'}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {editTab === 'notes' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-slate-900">Transaction Notes</h3>
                    <textarea className="w-full rounded-xl border border-slate-200 p-3 text-sm text-slate-700" rows={8} placeholder="Add notes about this transaction..." />
                  </div>
                )}

                {editTab === 'emails' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-slate-900">Email History</h3>
                    <p className="text-sm text-slate-500">No email history available for this transaction.</p>
                  </div>
                )}

                {editTab === 'summary' && (
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className="text-lg font-semibold text-slate-900">{isEditingRentalTransaction ? 'Rental Transaction Summary' : 'Transaction Summary'}</h3>
                      {editingId && !isEditingRentalTransaction && (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              if (!editingId) {
                                return;
                              }

                              if (!hasSummaryRelevantUnsavedChanges) {
                                void (async () => {
                                  setForceSummaryPreview(false);
                                  const statusLabel = (form.transaction_status || currentEditingRow?.transaction_status || '').trim() || 'Unknown';
                                  const isRegistered = statusLabel.toLowerCase() === 'registered';
                                  setSummaryPreviewNotice('Recalculating saved summary using backend rules...');
                                  try {
                                    await recalculateSavedTransactionSummary(
                                      editingId,
                                      isRegistered
                                        ? 'Saved summary recalculated using backend rules.'
                                        : `Recalculation complete. Current status is ${statusLabel}; Market Centre allocation and cap progression only apply once status is Registered.`
                                    );
                                  } catch (error) {
                                    setSummaryPreviewNotice(error instanceof Error ? error.message : 'Failed to recalculate saved summary');
                                  }
                                })();
                                return;
                              }
                              setForceSummaryPreview(true);
                              setSummaryPreviewNotice(
                                previewWouldChangeNumbers
                                  ? 'Preview mode active: these values are not saved yet. Click Save to apply them.'
                                  : 'Preview completed: no financial difference was detected from the currently saved summary for these edits.'
                              );
                            }}
                            disabled={isRecalculatingSummary}
                            className="rounded-lg border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-800 hover:bg-blue-100"
                            title="Recalculate unsaved preview values, or if there are no unsaved changes, recompute the saved backend summary."
                          >
                            {isRecalculatingSummary ? 'Recalculating...' : 'Recalculate Preview'}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                        <span className="font-semibold text-slate-900">Agent:</span> {editSummaryHeaderMeta.agentName}
                        {editSummaryHeaderMeta.moreAgents > 0 ? ` +${editSummaryHeaderMeta.moreAgents} more` : ''}
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                        <span className="font-semibold text-slate-900">Market Centre:</span> {editSummaryHeaderMeta.officeName}
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                        <span className="font-semibold text-slate-900">Side:</span> {editSummaryHeaderMeta.sideLabel}
                      </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                      <div className="grid grid-cols-2 gap-2 border-b border-slate-200 bg-slate-50/80 p-3 text-xs sm:grid-cols-6">
                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                          <p className="text-[11px] uppercase tracking-wide text-slate-500">Rows</p>
                          <p className="text-sm font-semibold text-slate-900">{calculatedSummaryRows.length}</p>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                          <p className="text-[11px] uppercase tracking-wide text-slate-500">GCI After Fees</p>
                          <p className="text-sm font-semibold text-slate-900">{toMoney(String(editSummaryTotals.gciAfterFees))}</p>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                          <p className="text-[11px] uppercase tracking-wide text-slate-500">Associate $</p>
                          <p className="text-sm font-semibold text-slate-900">{toMoney(String(editSummaryTotals.associate))}</p>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                          <p className="text-[11px] uppercase tracking-wide text-slate-500">Cap Contribution</p>
                          <p className="text-sm font-semibold text-slate-900">{toMoney(String(editSummaryTotals.capContribution))}</p>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                          <p className="text-[11px] uppercase tracking-wide text-slate-500">Market Centre $</p>
                          <p className="text-sm font-semibold text-slate-900">{toMoney(String(editSummaryTotals.marketCenter))}</p>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                          <p className="text-[11px] uppercase tracking-wide text-slate-500">Team $</p>
                          <p className="text-sm font-semibold text-slate-900">{toMoney(String(editSummaryTotals.team))}</p>
                        </div>
                      </div>
                      <p className="px-3 pb-2 pt-1 text-xs text-slate-500">Totals above aggregate all summary rows for this transaction.</p>
                      {calculatedSummaryRows.length > 1 && (
                        <div className="px-3 pb-3">
                          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Per-Agent Payout Snapshot</div>
                          <div className="flex gap-2 overflow-x-auto pb-1">
                            {calculatedSummaryRows.map((item, index) => (
                              <div key={`agent-pill-${item.id}`} className="min-w-[240px] rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                                <p className="truncate text-xs font-semibold text-slate-900">
                                  {item.agent_name || `Agent ${index + 1}`}
                                  <span className="ml-1 font-normal text-slate-500">({item.is_outside_agent ? 'Outside Agent' : normalizeSummarySide(item.transaction_side, '-')})</span>
                                </p>
                                {item.is_outside_agent ? (
                                  <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-600">
                                    <span>Role: <strong className="text-slate-800">Outside Agent</strong></span>
                                    <span>Split: <strong className="text-slate-800">{toPercent(Number(item.split_percentage ?? 0))}</strong></span>
                                    <span className="col-span-2">GCI Before Fees: <strong className="text-slate-800">{toMoney(item.transaction_gci_before_fees ?? '0')}</strong></span>
                                  </div>
                                ) : (
                                  <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-600">
                                    <span>Assoc: <strong className="text-slate-800">{toMoney(item.associate_dollar ?? '0')}</strong></span>
                                    <span>MC: <strong className="text-slate-800">{toMoney(item.market_center_dollar ?? '0')}</strong></span>
                                    <span>Team: <strong className="text-slate-800">{toMoney(item.team_dollar ?? '0')}</strong></span>
                                    <span>GCI: <strong className="text-slate-800">{toMoney(item.gci_after_fees_excl_vat ?? '0')}</strong></span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="max-h-[56vh] overflow-auto">
                      <table className="min-w-[1650px] w-full divide-y divide-slate-200 text-sm">
                        <thead className="sticky top-0 z-10 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                          <tr>
                            <th className="px-4 py-3 font-semibold">Agent Name</th>
                            <th className="px-4 py-3 font-semibold">Office Name</th>
                            <th className="px-4 py-3 font-semibold">Transaction Type</th>
                            <th className="px-4 py-3 font-semibold">Split %</th>
                            <th className="px-4 py-3 font-semibold">{isEditingRentalTransaction ? 'Variance % (N/A)' : 'Variance %'}</th>
                            <th className="px-4 py-3 font-semibold">GCI Before Fees</th>
                            <th className="px-4 py-3 font-semibold">{isEditingRentalTransaction ? 'Effective Split %' : 'Avg Comm %'}</th>
                            <th className="px-4 py-3 font-semibold">GCI After Fees</th>
                            <th className="px-4 py-3 font-semibold">Associate $</th>
                            <th className="px-4 py-3 font-semibold">Cap Contribution</th>
                            <th className="px-4 py-3 font-semibold">Market Centre $</th>
                            <th className="px-4 py-3 font-semibold">Team $</th>
                            <th className="px-4 py-3 font-semibold">PR</th>
                            <th className="px-4 py-3 font-semibold">GS</th>
                            <th className="px-4 py-3 font-semibold">PR + GS Total</th>
                            <th className="px-4 py-3 font-semibold">Cap Amount</th>
                            <th className="px-4 py-3 font-semibold">Cap Remaining</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white text-slate-800">
                          {calculatedSummaryRows.map((item) => (
                            <tr key={item.id} className="odd:bg-white even:bg-slate-50/30 hover:bg-blue-50/40">
                              <td className="px-4 py-3 font-medium text-slate-900">{item.agent_name || 'Unassigned'}</td>
                              <td className="px-4 py-3">{item.office_name || '-'}</td>
                              <td className="px-4 py-3">{item.is_outside_agent ? 'Outside Agent' : normalizeSummarySide(item.transaction_side, '-')}</td>
                              <td className="px-4 py-3">{toPercent(Number(item.split_percentage ?? 0))}</td>
                              <td className="px-4 py-3">{isEditingRentalTransaction ? '-' : toPercent(Number(item.variance_sale_list_pct ?? 0))}</td>
                              <td className="px-4 py-3">{toMoney(item.transaction_gci_before_fees ?? '0')}</td>
                              <td className="px-4 py-3">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : (isEditingRentalTransaction ? toPercent(Number(item.split_percentage ?? 0)) : toPercent(Number(item.average_commission_pct ?? 0)))}</td>
                              <td className="px-4 py-3">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : toMoney(item.gci_after_fees_excl_vat ?? '0')}</td>
                              <td className="px-4 py-3">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : toMoney(item.associate_dollar ?? '0')}</td>
                              <td className="px-4 py-3">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : toMoney(item.cap_contribution ?? item.market_center_dollar ?? '0')}</td>
                              <td className="px-4 py-3">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : toMoney(item.market_center_dollar ?? '0')}</td>
                              <td className="px-4 py-3">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : toMoney(item.team_dollar ?? '0')}</td>
                              <td className="px-4 py-3">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : toMoney(item.production_royalties ?? '0')}</td>
                              <td className="px-4 py-3">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : toMoney(item.growth_share ?? '0')}</td>
                              <td className="px-4 py-3">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : toMoney(item.total_pr_and_gs ?? '0')}</td>
                              <td className="px-4 py-3">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : toMoney(item.cap_amount ?? '0')}</td>
                              <td className="px-4 py-3">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : toMoney(item.display_cap_remaining ?? item.current_cap_remaining ?? item.cap_remaining ?? '0')}</td>
                            </tr>
                          ))}
                          {isEditSummaryLoading && (
                            <tr>
                              <td className="px-4 py-4 text-slate-500" colSpan={17}>
                                Loading latest calculated summary...
                              </td>
                            </tr>
                          )}
                          {!isEditSummaryLoading && calculatedSummaryRows.length === 0 && (
                            <tr>
                              <td className="px-4 py-4 text-slate-500" colSpan={17}>
                                No summary rows are available yet. Enter the required values to see a live preview, or save to load backend-calculated rows.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                      </div>
                    </div>
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                      {summaryPreviewNotice ? summaryPreviewNotice : (isShowingPreviewForEdit
                        ? 'Preview mode: these are unsaved values. If you are happy with this result, click Save to apply it.'
                        : currentEditingRow
                          ? (isEditingRentalTransaction
                            ? 'These values are calculated from rental payment details and participant splits.'
                            : 'These values are calculated and saved by the backend. Click Recalculate Preview to see how unsaved edits will affect this summary.')
                          : 'Complete price, role, and split fields to preview projected values before saving.')}
                    </div>
                  </div>
                )}

              </fieldset>
            </div>
          </div>
        </div>
      )}

      {view === 'summary' && !isRentalsCategory && (
        <section className="space-y-4">
          <div className="surface-card p-5 shadow-sm ring-1 ring-slate-200/70">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">{performanceLabel}</h3>
                <p className="mt-0.5 text-xs text-slate-500">Scoped to your active role and reporting window.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowSummaryFilters((current) => !current)}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  <UiIcon kind="filters" className="h-3.5 w-3.5" />
                  Filters
                </button>
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                  <UiIcon kind="window" className="h-3.5 w-3.5" />
                  Window: {periodLabel}
                </span>
              </div>
            </div>
            {showSummaryFilters && (
              <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                Summary filters follow your selected category, active context, and reporting window from backend aggregation.
              </div>
            )}
            <div className={`mt-4 grid grid-cols-1 gap-4 ${isAdminSummaryRole ? 'md:grid-cols-2 xl:grid-cols-4' : 'md:grid-cols-2 xl:grid-cols-2'}`}>
              <div className="rounded-xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 p-3.5">
                <p className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide text-slate-500"><UiIcon kind="tx" className="h-3.5 w-3.5" />Total Transactions</p>
                <p className="mt-2 text-3xl font-semibold leading-none text-slate-900">
                  {isSummaryLoading ? '...' : (isAdminSummaryRole ? adminContractsValue : mtdTransactionsValue).toLocaleString()}
                </p>
              </div>
              {isAdminSummaryRole && (
              <div className="rounded-xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 p-3.5">
                <p className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide text-slate-500"><UiIcon kind="value" className="h-3.5 w-3.5" />Total Sales Value</p>
                <p className="mt-2 text-3xl font-semibold leading-none text-slate-900">
                  {isSummaryLoading ? '...' : toMoney(String(mtdSalesValue))}
                </p>
              </div>
              )}
              {isAdminSummaryRole && (
              <div className="rounded-xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 p-3.5">
                <p className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide text-slate-500"><UiIcon kind="gci" className="h-3.5 w-3.5" />Total GCI</p>
                <p className="mt-2 text-3xl font-semibold leading-none text-slate-900">
                  {isSummaryLoading ? '...' : toMoney(String(adminTotalGciValue))}
                </p>
              </div>
              )}
              {isAdminSummaryRole && (
              <div className="rounded-xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 p-3.5">
                <p className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide text-slate-500"><UiIcon kind="split" className="h-3.5 w-3.5" />Average Split</p>
                <p className="mt-2 text-3xl font-semibold leading-none text-slate-900">
                  {isSummaryLoading ? '...' : `${mtdAverageSplitValue.toFixed(2)}%`}
                </p>
              </div>
              )}
              {!isAdminSummaryRole && (
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="inline-flex items-center gap-1 text-xs uppercase tracking-wide text-slate-500"><UiIcon kind="summary" className="h-3.5 w-3.5" />Scope</p>
                  <p className="mt-2 text-sm font-medium text-slate-700">
                    {isTeamSummaryRole
                      ? 'Team-scoped summary view'
                      : isIndividualAgentSummaryRole
                        ? 'My transactions summary view'
                        : 'Scoped transactions summary view'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {summaryWorkflowCues.length > 0 && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {summaryWorkflowCues.map((cue) => (
                  <div key={cue.title} className="rounded-xl border border-blue-200 bg-blue-50/70 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-blue-800">{cue.title}</p>
                    <p className="mt-1 text-sm text-blue-900">{cue.detail}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Next action</span>
                <button
                  type="button"
                  onClick={() => {
                    setView('register');
                    if (isOfficeAdmin) {
                      setStatus('Working');
                      setRegisterDateFilter('');
                      setRegisterDateFrom('');
                      setRegisterDateTo('');
                    }
                    setPage(1);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <UiIcon kind="register" className="h-3.5 w-3.5" />
                  {isOfficeAdmin ? 'Open Working Pipeline' : 'Investigate Register'}
                </button>
                {isRegionalAdmin && (
                  <button
                    type="button"
                    onClick={() => setView('month_end')}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100"
                  >
                    <UiIcon kind="calendar-clock" className="h-3.5 w-3.5" />
                    Open Month End
                  </button>
                )}
              </div>
            </div>
          )}

          {isAdminSummaryRole && (
          <div className={`grid grid-cols-1 gap-4 ${isRegionalAdmin ? 'xl:grid-cols-3' : 'xl:grid-cols-2'}`}>
            {isRegionalAdmin && (
              <div className="surface-card p-4 shadow-sm ring-1 ring-slate-200/70">
                <h3 className="text-sm font-semibold text-slate-900">Top 10 Market Centres This Month</h3>
                <div className="mt-3 space-y-2">
                  {parityTopMarketCenters.map((row, index) => (
                    <div key={`${row.market_center_name}-${index}`} className="grid grid-cols-12 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 transition-colors hover:bg-slate-50/70">
                      <span className="col-span-5 truncate text-sm text-slate-700"><span className="mr-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500">#{index + 1}</span>{row.market_center_name}</span>
                      <span className="col-span-2 text-xs text-slate-600">{Number(row.contracts ?? 0).toLocaleString()} tx</span>
                      <span className="col-span-5 text-sm font-semibold text-slate-900">GCI {toMoney(String(row.total_gci ?? 0))}</span>
                    </div>
                  ))}
                  {!isSummaryLoading && parityTopMarketCenters.length === 0 && (
                    <p className="text-sm text-slate-500">No market centre data in this month window.</p>
                  )}
                </div>
              </div>
            )}

            <div className="surface-card p-4 shadow-sm ring-1 ring-slate-200/70">
              <h3 className="text-sm font-semibold text-slate-900">Top 10 Associates This Month</h3>
              <div className="mt-3 space-y-2">
                {topIndividualAssociatesThisMonth.map((row, index) => (
                  <div key={`${row.associate_name}-${index}`} className="grid grid-cols-12 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 transition-colors hover:bg-slate-50/70">
                    <span className={isRegionalAdmin ? 'col-span-4 truncate text-sm text-slate-700' : 'col-span-5 truncate text-sm text-slate-700'}><span className="mr-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500">#{index + 1}</span>{row.associate_name}</span>
                    <span className={isRegionalAdmin ? 'col-span-3 truncate text-xs text-slate-600' : 'col-span-3 truncate text-xs text-slate-600'}>{row.team_name || 'No Team'}</span>
                    {isRegionalAdmin && <span className="col-span-2 truncate text-xs text-slate-600">{row.market_center}</span>}
                    <span className={isRegionalAdmin ? 'col-span-1 text-xs text-slate-600' : 'col-span-1 text-xs text-slate-600'}>{row.total_transactions.toLocaleString()} tx</span>
                    <span className={isRegionalAdmin ? 'col-span-2 text-sm font-semibold text-slate-900' : 'col-span-3 text-sm font-semibold text-slate-900'}>{toMoney(String(row.total_gci))}</span>
                  </div>
                ))}
                {!isSummaryLoading && topIndividualAssociatesThisMonth.length === 0 && (
                  <p className="text-sm text-slate-500">No associate data in this month window.</p>
                )}
              </div>
            </div>

            <div className="surface-card p-4 shadow-sm ring-1 ring-slate-200/70">
              <h3 className="text-sm font-semibold text-slate-900">Top 10 Teams This Month</h3>
              <div className="mt-3 space-y-2">
                {topTeamsThisMonth.map((row, index) => (
                  <div key={`${row.team_name}-${index}`} className="grid grid-cols-12 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 transition-colors hover:bg-slate-50/70">
                    <span className={isRegionalAdmin ? 'col-span-4 truncate text-sm text-slate-700' : 'col-span-6 truncate text-sm text-slate-700'}><span className="mr-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500">#{index + 1}</span>{row.team_name}</span>
                    {isRegionalAdmin && <span className="col-span-4 truncate text-xs text-slate-600">{row.market_center}</span>}
                    <span className={isRegionalAdmin ? 'col-span-1 text-xs text-slate-600' : 'col-span-2 text-xs text-slate-600'}>{row.total_transactions.toLocaleString()} tx</span>
                    <span className={isRegionalAdmin ? 'col-span-3 text-sm font-semibold text-slate-900' : 'col-span-4 text-sm font-semibold text-slate-900'}>{toMoney(String(row.total_gci))}</span>
                  </div>
                ))}
                {!isSummaryLoading && topTeamsThisMonth.length === 0 && (
                  <p className="text-sm text-slate-500">No team data in this month window.</p>
                )}
              </div>
            </div>
          </div>
          )}

          {isSummaryError && !summaryData && (
            <div className="surface-card p-4 text-amber-700">
              <p className="font-medium">Could not load summary metrics.</p>
              <button
                type="button"
                onClick={() => void refetchSummary()}
                className="mt-2 inline-flex items-center rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100"
              >
                Retry Summary
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="surface-card p-4 shadow-sm ring-1 ring-slate-200/70">
              <h3 className="text-sm font-semibold text-slate-900">By Status</h3>
              <div className="mt-3 space-y-2">
                {(() => {
                  const rows = summaryData?.by_status ?? [];
                  const maxCount = rows.reduce((max, row) => Math.max(max, row.count), 0);
                  return rows.map((row) => {
                    const widthPercent = maxCount > 0 ? Math.max(8, Math.round((row.count / maxCount) * 100)) : 8;
                    return (
                      <div key={row.label} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-700">{row.label}</span>
                          <span className="status-chip info">{row.count.toLocaleString()}</span>
                        </div>
                        <div className="mt-2 h-1.5 rounded-full bg-slate-100">
                          <div className="h-full rounded-full bg-slate-400" style={{ width: `${widthPercent}%` }} />
                        </div>
                      </div>
                    );
                  });
                })()}
                {!isSummaryLoading && (summaryData?.by_status.length ?? 0) === 0 && (
                  <p className="text-sm text-slate-500">No status data available for the current window.</p>
                )}
              </div>
            </div>

            <div className="surface-card p-4 shadow-sm ring-1 ring-slate-200/70">
              <h3 className="text-sm font-semibold text-slate-900">By Transaction Type</h3>
              <div className="mt-3 space-y-2">
                {(() => {
                  const rows = summaryData?.by_type ?? [];
                  const maxCount = rows.reduce((max, row) => Math.max(max, row.count), 0);
                  return rows.map((row) => {
                    const widthPercent = maxCount > 0 ? Math.max(8, Math.round((row.count / maxCount) * 100)) : 8;
                    return (
                      <div key={row.label} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-700">{row.label}</span>
                          <span className="status-chip info">{row.count.toLocaleString()}</span>
                        </div>
                        <div className="mt-2 h-1.5 rounded-full bg-slate-100">
                          <div className="h-full rounded-full bg-red-400" style={{ width: `${widthPercent}%` }} />
                        </div>
                      </div>
                    );
                  });
                })()}
                {!isSummaryLoading && (summaryData?.by_type.length ?? 0) === 0 && (
                  <p className="text-sm text-slate-500">No type data available for the current window.</p>
                )}
              </div>
            </div>
          </div>

          <div className="surface-card p-4 shadow-sm ring-1 ring-slate-200/70">
            <h3 className="text-sm font-semibold text-slate-900">Expected Closings (Next 120 Days)</h3>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {(summaryData?.expected_closings_90_days ?? []).map((row) => {
                const normalizedBucket = row.bucket.toLowerCase();
                const bucketIcon = normalizedBucket.includes('30')
                  ? 'calendar'
                  : normalizedBucket.includes('60')
                    ? 'calendar-clock'
                    : normalizedBucket.includes('90')
                      ? 'hourglass'
                      : 'timeline';
                return (
                  <div key={row.bucket} className="rounded-lg border border-slate-200 bg-white p-3">
                    <p className="inline-flex items-center gap-1 text-xs font-medium text-slate-500">
                      <UiIcon kind={bucketIcon} className="h-3.5 w-3.5" />
                      {row.bucket}
                    </p>
                    <p className="mt-1 text-lg font-semibold text-slate-900">{row.count.toLocaleString()} units</p>
                    <p className="mt-1 text-xs text-slate-600">Total GCI: {toMoney(String(row.total_gci ?? 0))}</p>
                  </div>
                );
              })}
              {!isSummaryLoading && (summaryData?.expected_closings_90_days.length ?? 0) === 0 && (
                <p className="text-sm text-slate-500">No expected closings in the configured forecast window.</p>
              )}
            </div>
          </div>
        </section>
      )}

      {view === 'month_end' && !isRentalsCategory && isRegionalAdmin && (
        <section className="space-y-4">
          <div className="surface-card p-4">
            <h3 className="text-base font-semibold text-slate-900">Month End FrontDoor Batch</h3>
            <p className="mt-1 text-sm text-slate-600">
              Run a test batch first to inspect exactly what will be sent and which items will error. Then decide whether to fix first or submit ready items as-is.
            </p>

            {monthEndWorkflowCues.length > 0 && (
              <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
                {monthEndWorkflowCues.map((cue) => (
                  <div key={cue.title} className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-800">{cue.title}</p>
                    <p className="mt-1 text-sm text-emerald-900">{cue.detail}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-600">Date From</span>
                <input
                  type="date"
                  value={monthEndDateFrom}
                  onChange={(event) => setMonthEndDateFrom(event.target.value)}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-600">Date To</span>
                <input
                  type="date"
                  value={monthEndDateTo}
                  onChange={(event) => setMonthEndDateTo(event.target.value)}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </label>
              <div className="md:col-span-2 flex items-end gap-2">
                <button
                  type="button"
                  onClick={() => void runMonthEndPreview()}
                  disabled={isPreviewingMonthEnd || isSubmittingMonthEnd}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isPreviewingMonthEnd ? 'Testing Batch...' : 'Test Batch'}
                </button>
                <button
                  type="button"
                  onClick={() => void submitMonthEndBatch()}
                  disabled={
                    isSubmittingMonthEnd
                    || isPreviewingMonthEnd
                    || !monthEndPreview
                    || selectedMonthEndItemIds.length <= 0
                    || selectedMonthEndReadyCount <= 0
                    || (selectedMonthEndErrorCount > 0 && !allowSubmitWithErrors)
                  }
                  className="primary-btn disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSubmittingMonthEnd ? 'Submitting...' : 'Submit To FrontDoor'}
                </button>
              </div>
            </div>

            <label className="mt-3 inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={allowSubmitWithErrors}
                onChange={(event) => setAllowSubmitWithErrors(event.target.checked)}
                className="h-4 w-4 rounded border-slate-300"
              />
              Allow submit as-is when errors exist (only ready items will be sent)
            </label>

            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-4">
              <label className="flex flex-col gap-1 md:col-span-2 lg:col-span-2">
                <span className="text-xs font-medium text-slate-600">Search</span>
                <input
                  type="text"
                  value={monthEndSearch}
                  onChange={(event) => setMonthEndSearch(event.target.value)}
                  placeholder="Transaction, listing, associate, market centre"
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-600">Preview Status</span>
                <select
                  value={monthEndPreviewStatusFilter}
                  onChange={(event) => setMonthEndPreviewStatusFilter(event.target.value as 'all' | 'ready' | 'error')}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="all">All</option>
                  <option value="ready">Ready</option>
                  <option value="error">Error</option>
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-600">Fed Status</span>
                <select
                  value={monthEndFedStatusFilter}
                  onChange={(event) => setMonthEndFedStatusFilter(event.target.value as 'all' | 'never' | 'fed' | 'submitted' | 'failed' | 'skipped')}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="all">All</option>
                  <option value="never">Never Fed</option>
                  <option value="fed">Fed At Least Once</option>
                  <option value="submitted">Last Submitted</option>
                  <option value="failed">Last Failed</option>
                  <option value="skipped">Last Skipped</option>
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-600">Market Centre</span>
                <input
                  type="text"
                  list="month-end-active-market-centers"
                  value={monthEndMarketCenterFilter}
                  onChange={(event) => setMonthEndMarketCenterFilter(event.target.value)}
                  placeholder="Search active market centres"
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <datalist id="month-end-active-market-centers">
                  {(monthEndMarketCentersData?.items ?? []).map((item) => (
                    <option key={item.id} value={item.name} label={`${item.name} (${item.source_market_center_id})`} />
                  ))}
                </datalist>
                <span className="text-[11px] text-slate-500">
                  {isMonthEndMarketCentersLoading
                    ? 'Loading active market centres...'
                    : `Active market centre matches: ${(monthEndMarketCentersData?.items ?? []).length}`}
                </span>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-600">Associate</span>
                <input
                  type="text"
                  value={monthEndAssociateFilter}
                  onChange={(event) => setMonthEndAssociateFilter(event.target.value)}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-600">Listing</span>
                <input
                  type="text"
                  value={monthEndListingFilter}
                  onChange={(event) => setMonthEndListingFilter(event.target.value)}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-600">Transaction</span>
                <input
                  type="text"
                  value={monthEndTransactionFilter}
                  onChange={(event) => setMonthEndTransactionFilter(event.target.value)}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </label>
            </div>

            {monthEndError && (
              <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{monthEndError}</p>
            )}
          </div>

          {monthEndPreview && (
            <div className="surface-card p-4 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="status-chip info">Total: {monthEndPreview.total_candidates.toLocaleString()}</span>
                <span className="status-chip good">Ready: {monthEndPreview.ready_to_submit.toLocaleString()}</span>
                <span className="status-chip">Errors/Skipped: {monthEndPreview.will_be_skipped.toLocaleString()}</span>
                <span className="status-chip">Window: {monthEndPreview.date_from} to {monthEndPreview.date_to}</span>
                <span className="status-chip info">Selected: {selectedMonthEndItemIds.length.toLocaleString()}</span>
                <span className="status-chip good">Selected Ready: {selectedMonthEndReadyCount.toLocaleString()}</span>
                <span className="status-chip">Selected Errors: {selectedMonthEndErrorCount.toLocaleString()}</span>
                <span className="status-chip info">Selected GCI: {toMoney(String(selectedMonthEndGciTotal))}</span>
                <span className="status-chip good">Feed GCI (Ready): {toMoney(String(selectedMonthEndFeedGciTotal))}</span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  onClick={selectAllVisibleMonthEndItems}
                >
                  Select All Visible
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  onClick={selectReadyMonthEndItems}
                >
                  Select Ready
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  onClick={clearMonthEndSelection}
                >
                  Clear Selection
                </button>
              </div>

              <div className="overflow-auto rounded-lg border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-600">
                    <tr>
                      <th className="px-3 py-2">Select</th>
                      <th className="px-3 py-2">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 hover:text-slate-900"
                          onClick={() => toggleMonthEndSort('status')}
                        >
                          Status <span>{monthEndSortIndicator('status')}</span>
                        </button>
                      </th>
                      <th className="px-3 py-2">
                        <button type="button" className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => toggleMonthEndSort('transaction')}>
                          Transaction <span>{monthEndSortIndicator('transaction')}</span>
                        </button>
                      </th>
                      <th className="px-3 py-2">
                        <button type="button" className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => toggleMonthEndSort('associate')}>
                          Associate <span>{monthEndSortIndicator('associate')}</span>
                        </button>
                      </th>
                      <th className="px-3 py-2">
                        <button type="button" className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => toggleMonthEndSort('market_center')}>
                          Market Center <span>{monthEndSortIndicator('market_center')}</span>
                        </button>
                      </th>
                      <th className="px-3 py-2">
                        <button type="button" className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => toggleMonthEndSort('listing')}>
                          Listing <span>{monthEndSortIndicator('listing')}</span>
                        </button>
                      </th>
                      <th className="px-3 py-2">
                        <button type="button" className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => toggleMonthEndSort('gci')}>
                          GCI <span>{monthEndSortIndicator('gci')}</span>
                        </button>
                      </th>
                      <th className="px-3 py-2">
                        <button type="button" className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => toggleMonthEndSort('sales_price')}>
                          Sales Price <span>{monthEndSortIndicator('sales_price')}</span>
                        </button>
                      </th>
                      <th className="px-3 py-2">
                        <button type="button" className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => toggleMonthEndSort('reporting_date')}>
                          Reporting Date <span>{monthEndSortIndicator('reporting_date')}</span>
                        </button>
                      </th>
                      <th className="px-3 py-2">
                        <button type="button" className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => toggleMonthEndSort('fed_status')}>
                          Fed Status <span>{monthEndSortIndicator('fed_status')}</span>
                        </button>
                      </th>
                      <th className="px-3 py-2">
                        <button type="button" className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => toggleMonthEndSort('fed_at')}>
                          Fed At <span>{monthEndSortIndicator('fed_at')}</span>
                        </button>
                      </th>
                      <th className="px-3 py-2">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white text-slate-800">
                    {sortedMonthEndItems.map((item) => (
                      <tr key={`${item.transaction_agent_id}-${item.region_transaction_id}`}>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={monthEndSelectedSet.has(item.transaction_agent_id)}
                            onChange={() => toggleMonthEndItemSelection(item.transaction_agent_id)}
                            className="h-4 w-4 rounded border-slate-300"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${item.status === 'ready' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                            {item.status === 'ready' ? 'Ready' : 'Error'}
                          </span>
                        </td>
                        <td className="px-3 py-2">{item.transaction_number ?? item.transaction_id}</td>
                        <td className="px-3 py-2">{item.associate_name ?? '-'}</td>
                        <td className="px-3 py-2">{item.market_center_name ?? '-'}</td>
                        <td className="px-3 py-2">{item.listing_number ?? '-'}</td>
                        <td className="px-3 py-2">{item.gci_amount !== null ? toMoney(String(item.gci_amount)) : '-'}</td>
                        <td className="px-3 py-2">{toMoney(String(item.sales_price ?? 0))}</td>
                        <td className="px-3 py-2">{item.effective_reporting_date}</td>
                        <td className="px-3 py-2">{item.last_submission_status ?? 'Never fed'}</td>
                        <td className="px-3 py-2">{toShortDateTime(item.last_submitted_at)}</td>
                        <td className="px-3 py-2">
                          {item.error
                            ?? (item.last_submission_status === 'failed'
                              ? (item.last_submission_error ?? 'Last feed failed')
                              : 'Will be submitted')}
                        </td>
                      </tr>
                    ))}
                    {monthEndPreview.items.length === 0 && (
                      <tr>
                        <td className="px-3 py-4 text-slate-500" colSpan={12}>No records returned for the selected period.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {monthEndSubmission && (
            <div className="surface-card p-4">
              <h4 className="text-sm font-semibold text-slate-900">Submission Result</h4>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="status-chip info">Run ID: {monthEndSubmission.run_id}</span>
                <span className="status-chip good">Submitted: {monthEndSubmission.submitted}</span>
                <span className="status-chip">Failed: {monthEndSubmission.failed}</span>
                <span className="status-chip">Skipped: {monthEndSubmission.skipped}</span>
                <span className="status-chip">Total: {monthEndSubmission.total}</span>
              </div>
            </div>
          )}
        </section>
      )}

      {view === 'register' && (
      <section className="surface-card p-4 md:p-5">
        <div className="mb-3 flex items-center gap-2">
          <p className="inline-flex items-center gap-1 text-xs font-medium text-slate-600">
            <UiIcon kind="filters" className="h-3.5 w-3.5" />
            Filters
          </p>
        </div>
        <div>
          <input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder={isRentalsCategory ? 'Search rental transaction, address, market centre, agent, suburb, city, status or type' : 'Search transaction, listing, market centre, agent, suburb, city, status or type'}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-0"
          />
        </div>

        <div className="my-3 border-t border-slate-200" />

        {isTransactionsUxEnhanced && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Quick Presets</span>
            <button
              type="button"
              onClick={() => {
                setStatus('Submitted');
                setRegisterDateFilter('');
                setRegisterDateFrom('');
                setRegisterDateTo('');
                setPage(1);
              }}
              className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Submitted Queue
            </button>
            <button
              type="button"
              onClick={() => {
                const now = new Date();
                const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
                const today = now.toISOString().slice(0, 10);
                setStatus('Registered');
                setRegisterDateFilter('status_change_date');
                setRegisterDateFrom(monthStart);
                setRegisterDateTo(today);
                setPage(1);
              }}
              className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Registered MTD
            </button>
            <button
              type="button"
              onClick={() => {
                setStatus('Working');
                setRegisterDateFilter('');
                setRegisterDateFrom('');
                setRegisterDateTo('');
                setPage(1);
              }}
              className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Working Pipeline
            </button>
            {activeContext?.marketCenter && (
              <button
                type="button"
                onClick={() => {
                  setMarketCenterQuickFilter(activeContext.marketCenter ?? '');
                  setPage(1);
                }}
                className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                My MC
              </button>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-end gap-2 md:gap-3">
          <label className="flex w-full flex-col gap-1 sm:w-[190px]">
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">Status</span>
            <select
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
              className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900"
            >
              <option value="">All statuses</option>
              {TRANSACTION_STATUSES.map((statusValue) => (
                <option key={statusValue} value={statusValue}>{statusValue}</option>
              ))}
            </select>
          </label>

          {isTransactionsUxEnhanced && (
            <label className="flex w-full flex-col gap-1 sm:w-[190px]">
              <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">Type</span>
              <select
                value={typeFilter}
                onChange={(event) => {
                  setTypeFilter(event.target.value);
                  setPage(1);
                }}
                className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900"
              >
                <option value="">All types</option>
                {TRANSACTION_TYPE_FILTER_OPTIONS.map((typeOption) => (
                  <option key={typeOption} value={typeOption}>{typeOption}</option>
                ))}
              </select>
            </label>
          )}

          {isTransactionsUxEnhanced && (
            <label className="flex w-full flex-col gap-1 sm:w-[220px]">
              <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">Agent Contains</span>
              <input
                value={agentQuickFilter}
                onChange={(event) => {
                  setAgentQuickFilter(event.target.value);
                  setPage(1);
                }}
                placeholder="Name or source associate ID"
                className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900"
              />
            </label>
          )}

          {isTransactionsUxEnhanced && (
            <label className="flex w-full flex-col gap-1 sm:w-[220px]">
              <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">Market Centre Contains</span>
              <input
                value={marketCenterQuickFilter}
                onChange={(event) => {
                  setMarketCenterQuickFilter(event.target.value);
                  setPage(1);
                }}
                placeholder="Office / market centre name"
                className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900"
              />
            </label>
          )}

          <label className="flex w-full flex-col gap-1 sm:w-[190px]">
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">Date Basis</span>
            <select
              value={registerDateFilter}
              onChange={(event) => {
                const nextValue = event.target.value as RegisterDateFilter;
                setRegisterDateFilter(nextValue);
                if (!nextValue) {
                  setRegisterDateFrom('');
                  setRegisterDateTo('');
                }
                setPage(1);
              }}
              className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900"
            >
              <option value="">No date filter</option>
              <option value="transaction_date">Transaction Date</option>
              <option value="status_change_date">Status Change Date</option>
            </select>
          </label>

          <label className="flex w-full flex-col gap-1 sm:w-[190px]">
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">Date From</span>
            <input
              type="date"
              value={registerDateFrom}
              disabled={!registerDateFilter}
              onChange={(event) => {
                setRegisterDateFrom(event.target.value);
                setPage(1);
              }}
              className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 disabled:cursor-not-allowed disabled:bg-slate-50"
            />
          </label>

          <label className="flex w-full flex-col gap-1 sm:w-[190px]">
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">Date To</span>
            <input
              type="date"
              value={registerDateTo}
              disabled={!registerDateFilter}
              onChange={(event) => {
                setRegisterDateTo(event.target.value);
                setPage(1);
              }}
              className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 disabled:cursor-not-allowed disabled:bg-slate-50"
            />
          </label>

          <button
            type="button"
            onClick={clearRegisterFilters}
            className="h-10 rounded-lg border border-slate-300 px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 sm:self-end"
          >
            Clear Filters
          </button>

          {isTransactionsUxEnhanced && (
            <label className="flex w-full flex-col gap-1 sm:w-[120px] sm:self-end">
              <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">Page Size</span>
              <select
                value={String(pageSize)}
                onChange={(event) => {
                  const nextPageSize = Number(event.target.value);
                  if (!Number.isFinite(nextPageSize) || nextPageSize <= 0) return;
                  setPageSize(nextPageSize);
                  setPage(1);
                }}
                className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900"
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </label>
          )}

          <span className="w-full text-xs text-slate-600 sm:ml-auto sm:w-auto sm:self-end sm:pb-0.5">Page {page} of {totalPages}</span>
          {isTransactionsUxEnhanced && (
            <span className="w-full text-xs text-slate-500 sm:w-auto sm:self-end sm:pb-0.5">Showing {shownStart.toLocaleString()}-{shownEnd.toLocaleString()} of {(data?.total ?? 0).toLocaleString()}</span>
          )}
        </div>

        <div className="mt-4 overflow-auto rounded-lg border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-3 py-2">
                  <button type="button" className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => toggleRegisterSort('transaction')}>
                    Transaction <span>{sortIndicator('transaction')}</span>
                  </button>
                </th>
                <th className="px-3 py-2">
                  <button type="button" className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => toggleRegisterSort('associate')}>
                    Agent Name <span>{sortIndicator('associate')}</span>
                  </button>
                </th>
                <th className="px-3 py-2">
                  <button type="button" className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => toggleRegisterSort('market_center')}>
                    Office Name <span>{sortIndicator('market_center')}</span>
                  </button>
                </th>
                <th className="px-3 py-2">
                  <button type="button" className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => toggleRegisterSort('type')}>
                    Transaction Type <span>{sortIndicator('type')}</span>
                  </button>
                </th>
                <th className="px-3 py-2">
                  <button type="button" className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => toggleRegisterSort('status')}>
                    Status <span>{sortIndicator('status')}</span>
                  </button>
                </th>
                <th className="px-3 py-2">
                  <button type="button" className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => toggleRegisterSort('created_date')}>
                    Transaction Date <span>{sortIndicator('created_date')}</span>
                  </button>
                </th>
                <th className="px-3 py-2">
                  <button type="button" className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => toggleRegisterSort('status_change_date')}>
                    Status Change Date <span>{sortIndicator('status_change_date')}</span>
                  </button>
                </th>
                <th className="px-3 py-2 text-right">
                  <button type="button" className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => toggleRegisterSort('sales_price')}>
                    {isRentalsCategory ? 'Gross Commission' : 'Sales Price'} <span>{sortIndicator('sales_price')}</span>
                  </button>
                </th>
                <th className="px-3 py-2 text-right">
                  <button type="button" className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => toggleRegisterSort('net_comm')}>
                    {isRentalsCategory ? 'Agent Net Amount' : 'Net Comm'} <span>{sortIndicator('net_comm')}</span>
                  </button>
                </th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white text-slate-800">
              {isLoading && (
                <tr>
                  <td className="px-3 py-5 text-slate-500" colSpan={10}>Loading transactions and applying your current filters...</td>
                </tr>
              )}

              {isError && !data && (
                <tr>
                  <td className="px-3 py-5 text-amber-700" colSpan={10}>
                    Could not load transactions from backend API.
                    <button
                      type="button"
                      onClick={() => void refetch()}
                      className="ml-2 inline-flex items-center rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100"
                    >
                      Retry
                    </button>
                  </td>
                </tr>
              )}

              {!isLoading && !isError && filteredItems.length === 0 && (
                <tr>
                  <td className="px-3 py-5" colSpan={10}>
                    <div className="space-y-2 text-slate-600">
                      <p className="font-medium text-slate-700">No transactions match the current filters.</p>
                      {isTransactionsUxEnhanced && activeRegisterFilterLabels.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {activeRegisterFilterLabels.map((label) => (
                            <span key={label} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600">
                              {label}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={clearRegisterFilters}
                          className="inline-flex items-center rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        >
                          Reset Filters
                        </button>
                        <span className="text-xs text-slate-500">Tip: broaden date range or clear status/type filters.</span>
                      </div>
                    </div>
                  </td>
                </tr>
              )}

              {sortedItems.map((item) => (
                <tr key={item.id}>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => setDetailViewId(item.id)}
                      className="font-medium text-slate-900 hover:text-red-700"
                    >
                      {item.transaction_number ?? item.source_transaction_id}
                    </button>
                    <div className="text-xs text-slate-500">
                      Listing:{' '}
                      {item.source_listing_id ? (
                        <a
                          href={`/listings?review=${encodeURIComponent(item.source_listing_id)}`}
                          className="font-medium text-blue-700 hover:text-blue-800"
                        >
                          {item.listing_number ?? item.source_listing_id}
                        </a>
                      ) : (
                        item.listing_number ?? '-'
                      )}
                    </div>
                    <div className="text-[11px] text-slate-400">ID: {item.source_transaction_id}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium">
                      {item.agents.length > 0
                        ? item.agents.map((a) => getTransactionAgentDisplayName(a)).join(', ')
                        : '-'}
                    </div>
                    <div className="text-xs text-slate-500 truncate max-w-64">{item.address ?? '-'}</div>
                    <div className="text-[11px] text-slate-400">
                      {item.suburb ?? '-'}{item.suburb && item.city ? ', ' : ''}{item.city ?? ''}
                    </div>
                  </td>
                  <td className="px-3 py-2">{item.market_center_name ?? '-'}</td>
                  <td className="px-3 py-2">{getDisplayTransactionType(item)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${transactionStatusClass(item.transaction_status)}`}>
                      {item.transaction_status ?? '-'}
                    </span>
                  </td>
                  <td className="px-3 py-2">{toShortDate(item.transaction_date ?? item.created_at ?? null)}</td>
                  <td className="px-3 py-2">{toShortDate(item.status_change_date)}</td>
                  <td className="px-3 py-2 text-right font-medium tabular-nums">{toMoney(isRentalsCategory ? (item.total_gci ?? item.sales_price) : item.sales_price)}</td>
                  <td className="px-3 py-2 text-right font-medium tabular-nums">{toMoney(item.net_comm)}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <button className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50" type="button" onClick={() => setDetailViewId(item.id)}>
                        <UiIcon kind="eye" className="h-3.5 w-3.5" />
                        View
                      </button>
                      {item.can_edit && getSalesStatusEditPolicy(!isRentalTransactionItem(item), item.transaction_status, isRegionalAdmin).canOpenEditor && (
                        <button className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50" type="button" onClick={() => openEditForm(item)}>
                          <UiIcon kind="edit" className="h-3.5 w-3.5" />
                          Edit
                        </button>
                      )}
                      <button className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50" type="button" onClick={() => setQuickSummaryRow(item)}>
                        <UiIcon kind="file" className="h-3.5 w-3.5" />
                        Summary
                      </button>
                      {isTransactionsUxEnhanced && (
                        <button
                          className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                          type="button"
                          onClick={() => void copyTransactionReference(item)}
                        >
                          {copiedTransactionId === item.id ? 'Copied' : 'Copy Tx#'}
                        </button>
                      )}
                      {isTransactionsUxEnhanced && !item.can_edit && (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">Read only</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            type="button"
            onClick={() => setPage((prev) => prev - 1)}
            disabled={!canGoPrev}
          >
            Previous
          </button>
          <button
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            type="button"
            onClick={() => setPage((prev) => prev + 1)}
            disabled={!canGoNext}
          >
            Next
          </button>
        </div>
      </section>
      )}

      {quickSummaryRow && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/40 p-3 md:p-4">
          <div className="mx-auto w-full max-w-7xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between rounded-t-2xl border-b border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50 px-4 py-3">
              <h3 className="text-lg font-semibold text-slate-900">Quick Transaction Summary</h3>
              <button className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs hover:bg-slate-50" type="button" onClick={() => setQuickSummaryRow(null)}>
                Close
              </button>
            </div>
            <div className="max-h-[82vh] overflow-auto p-4">
              <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                Transaction: <strong>{quickSummaryRow.transaction_number ?? quickSummaryRow.source_transaction_id}</strong>
              </div>
              <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                  <span className="font-semibold text-slate-900">Agent:</span> {quickSummaryHeaderMeta.agentName}
                  {quickSummaryHeaderMeta.moreAgents > 0 ? ` +${quickSummaryHeaderMeta.moreAgents} more` : ''}
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                  <span className="font-semibold text-slate-900">Market Centre:</span> {quickSummaryHeaderMeta.officeName}
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                  <span className="font-semibold text-slate-900">Side:</span> {quickSummaryHeaderMeta.sideLabel}
                </div>
              </div>
              <div className="mb-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-6">
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Rows</p>
                  <p className="text-sm font-semibold text-slate-900">{quickSummaryCalculatedRows.length}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">GCI After Fees</p>
                  <p className="text-sm font-semibold text-slate-900">{toMoney(String(quickSummaryTotals.gciAfterFees))}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Associate $</p>
                  <p className="text-sm font-semibold text-slate-900">{toMoney(String(quickSummaryTotals.associate))}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Cap Contribution</p>
                  <p className="text-sm font-semibold text-slate-900">{toMoney(String(quickSummaryTotals.capContribution))}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Market Centre $</p>
                  <p className="text-sm font-semibold text-slate-900">{toMoney(String(quickSummaryTotals.marketCenter))}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Team $</p>
                  <p className="text-sm font-semibold text-slate-900">{toMoney(String(quickSummaryTotals.team))}</p>
                </div>
              </div>
              <p className="px-1 pb-2 pt-1 text-xs text-slate-500">Totals above aggregate all summary rows for this transaction.</p>
              {quickSummaryCalculatedRows.length > 1 && (
                <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Per-Agent Payout Snapshot</div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                    {quickSummaryCalculatedRows.map((item, index) => (
                      <div key={`quick-agent-pill-${item.id}`} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                        <p className="truncate text-xs font-semibold text-slate-900">
                          {item.agent_name || `Agent ${index + 1}`}
                          <span className="ml-1 font-normal text-slate-500">({item.is_outside_agent ? 'Outside Agent' : normalizeSummarySide(item.transaction_side, '-')})</span>
                        </p>
                        {item.is_outside_agent ? (
                          <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-600">
                            <span>Role: <strong className="text-slate-800">Outside Agent</strong></span>
                            <span>Split: <strong className="text-slate-800">{toPercent(Number(item.split_percentage ?? 0))}</strong></span>
                            <span className="col-span-2">GCI Before Fees: <strong className="text-slate-800">{toMoney(item.transaction_gci_before_fees ?? '0')}</strong></span>
                          </div>
                        ) : (
                          <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-600">
                            <span>Assoc: <strong className="text-slate-800">{toMoney(item.associate_dollar ?? '0')}</strong></span>
                            <span>MC: <strong className="text-slate-800">{toMoney(item.market_center_dollar ?? '0')}</strong></span>
                            <span>Team: <strong className="text-slate-800">{toMoney(item.team_dollar ?? '0')}</strong></span>
                            <span>GCI: <strong className="text-slate-800">{toMoney(item.gci_after_fees_excl_vat ?? '0')}</strong></span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="overflow-auto rounded-xl border border-slate-200">
              <table className="min-w-[1650px] w-full divide-y divide-slate-200 text-sm">
                <thead className="sticky top-0 z-10 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 whitespace-nowrap">Agent Name</th>
                    <th className="px-3 py-2 whitespace-nowrap">Office Name</th>
                    <th className="px-3 py-2 whitespace-nowrap">Transaction Type</th>
                    <th className="px-3 py-2 whitespace-nowrap">Split</th>
                    <th className="px-3 py-2 whitespace-nowrap">Variance %</th>
                    <th className="px-3 py-2 whitespace-nowrap">GCI Before Fees</th>
                    <th className="px-3 py-2 whitespace-nowrap">Avg Comm %</th>
                    <th className="px-3 py-2 whitespace-nowrap">GCI After Fees</th>
                    <th className="px-3 py-2 whitespace-nowrap">Associate $</th>
                    <th className="px-3 py-2 whitespace-nowrap">Cap Contribution</th>
                    <th className="px-3 py-2 whitespace-nowrap">Market Centre $</th>
                    <th className="px-3 py-2 whitespace-nowrap">Team $</th>
                    <th className="px-3 py-2 whitespace-nowrap">PR</th>
                    <th className="px-3 py-2 whitespace-nowrap">GS</th>
                    <th className="px-3 py-2 whitespace-nowrap">PR + GS Total</th>
                    <th className="px-3 py-2 whitespace-nowrap">Cap Amount</th>
                    <th className="px-3 py-2 whitespace-nowrap">Cap Remaining</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white text-slate-800">
                  {quickSummaryCalculatedRows.map((item) => (
                    <tr key={item.id} className="odd:bg-white even:bg-slate-50/30 hover:bg-blue-50/40">
                      <td className="px-3 py-2 whitespace-nowrap font-medium text-slate-900">{item.agent_name || 'Unassigned'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{item.office_name || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{item.is_outside_agent ? 'Outside Agent' : normalizeSummarySide(item.transaction_side, '-')}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{toPercent(Number(item.split_percentage ?? 0))}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{toPercent(Number(item.variance_sale_list_pct ?? 0))}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{toMoney(item.transaction_gci_before_fees ?? '0')}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : toPercent(Number(item.average_commission_pct ?? 0))}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : toMoney(item.gci_after_fees_excl_vat ?? '0')}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : toMoney(item.associate_dollar ?? '0')}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : toMoney(item.cap_contribution ?? item.market_center_dollar ?? '0')}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : toMoney(item.market_center_dollar ?? '0')}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : toMoney(item.team_dollar ?? '0')}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : toMoney(item.production_royalties ?? '0')}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : toMoney(item.growth_share ?? '0')}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : toMoney(item.total_pr_and_gs ?? '0')}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : toMoney(item.cap_amount ?? '0')}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{item.is_outside_agent ? <span className="text-slate-400">N/A</span> : toMoney(item.display_cap_remaining ?? item.current_cap_remaining ?? item.cap_remaining ?? '0')}</td>
                    </tr>
                  ))}
                  {isQuickSummaryLoading && (
                    <tr>
                      <td className="px-3 py-4 text-slate-500" colSpan={17}>Loading latest summary...</td>
                    </tr>
                  )}
                  {!isQuickSummaryLoading && quickSummaryCalculatedRows.length === 0 && (
                    <tr>
                      <td className="px-3 py-4 text-slate-500" colSpan={17}>No calculated rows found for this transaction yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Detail View Modal */}
      {directoryManagerOpen && <ProfessionalDirectoryManager onClose={() => setDirectoryManagerOpen(false)} />}
      {partyValidationFields.length > 0 && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/50 p-4" role="dialog" aria-modal="true" aria-labelledby="sales-party-validation-title">
          <div className="w-full max-w-lg space-y-4 rounded-lg bg-white p-5 shadow-xl">
            <div>
              <h2 id="sales-party-validation-title" className="text-lg font-semibold text-slate-900">Complete Sales parties</h2>
              <p className="mt-1 text-sm text-slate-600">The transaction status has not changed. Complete these fields and try again.</p>
            </div>
            <ul className="max-h-64 space-y-2 overflow-y-auto text-sm text-slate-800">
              {partyValidationFields.map((missing, index) => <li key={`${missing.fieldPath}-${index}`}><span className="font-medium">{missing.party}:</span> {missing.field}</li>)}
            </ul>
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" onClick={() => setPartyValidationFields([])}>Close</button>
              <button type="button" className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white" onClick={() => { setEditTab(getPartyCorrectionTab(partyValidationFields[0]?.fieldPath ?? 'parties')); setPartyValidationFields([]); }}>
                Go to {getPartyCorrectionTab(partyValidationFields[0]?.fieldPath ?? 'parties') === 'details' ? 'Transaction Details' : 'Transaction Parties'}
              </button>
            </div>
          </div>
        </div>
      )}
      {detailViewId && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm">
          <div className="absolute inset-4 rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden flex flex-col">
            <div className="flex-1 overflow-auto p-6">
              <TransactionDetailView transactionId={detailViewId} onClose={() => setDetailViewId(null)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
